// ─────────────────────────────────────────────────────────────────────────────
// DiariesSection — Daily Site Diary register.
// Construction Engineering Cockpit aesthetic. Token-driven, primitive-driven.
// Logic preserved from original; presentation rebuilt on ./ui.jsx primitives.
// ─────────────────────────────────────────────────────────────────────────────

import React, { memo, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { tokens } from "./theme";
import {
  Card, BlueprintGrid, Reveal, Eyebrow,
  Input, Select, Textarea, FormRow, FormGroupDivider,
  Button,
  Stat,
  Table, Th, Td,
} from "./ui";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { openHtmlWindow } from "../utils/printDoc";
import {
  SERVICE_TYPES,
  isServiceActivity,
  serviceMeta,
  CLEARANCE_SUBTYPES,
  clearanceSubTypeMeta,
  EXCAVATION_CONDITIONS,
  excavationConditionMeta,
} from "./VisualProgressTracker";

const t = tokens;

const DiaryBadge = memo(function DiaryBadge({ children, variant = "default", size = "sm" }) {
  const map = {
    default: {
      background: t.color.surface,
      color: t.color.inkMuted,
      border: t.color.border,
    },
    success: {
      background: t.color.successSoft || t.color.surface,
      color: t.color.success || t.color.ink,
      border: t.color.successDim || t.color.border,
    },
    warning: {
      background: t.color.warningSoft || t.color.surface,
      color: t.color.warning || t.color.ink,
      border: t.color.warningDim || t.color.border,
    },
    info: {
      background: t.color.infoSoft || t.color.surface,
      color: t.color.info || t.color.primary || t.color.ink,
      border: t.color.infoDim || t.color.border,
    },
  };

  const s = map[variant] || map.default;

  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: size === "sm" ? "2px 8px" : "1px 6px",
      borderRadius: 999,
      fontSize: size === "sm" ? 11 : 10,
      fontWeight: 700,
      lineHeight: 1.4,
      whiteSpace: "nowrap",
      background: s.background,
      color: s.color,
      border: `1px solid ${s.border}`,
      fontFamily: t.font.body,
    }}>
      {children}
    </span>
  );
});

const DiaryEmpty = memo(function DiaryEmpty({ title = "No records", message = "No items to display." }) {
  return (
    <div style={{
      padding: "28px 18px",
      borderRadius: t.radius.md || t.radius.sm,
      border: `1px dashed ${t.color.border}`,
      background: t.color.surface,
      textAlign: "center",
      display: "grid",
      gap: 6,
    }}>
      <div style={{
        fontFamily: t.font.display || t.font.body,
        fontSize: 15,
        fontWeight: 700,
        color: t.color.ink,
      }}>
        {title}
      </div>
      <div style={{
        fontSize: 12,
        lineHeight: 1.5,
        color: t.color.inkMuted,
        maxWidth: 520,
        margin: "0 auto",
      }}>
        {message}
      </div>
    </div>
  );
});

// ─── Page-specific icons ─────────────────────────────────────────────────────
const I = {
  add:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  save:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  cancel: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  copy:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  view:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  edit:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  rain:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 16.2A4.5 4.5 0 0 0 17.5 8h-1.8A7 7 0 1 0 4 14.9"/><line x1="16" y1="14" x2="16" y2="21"/><line x1="8" y1="16" x2="8" y2="21"/><line x1="12" y1="15" x2="12" y2="22"/><line x1="20" y1="16" x2="20" y2="21"/></svg>,
  pdf:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>,
  md:     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>,
};

// ═════════════════════════════════════════════════════════════════════════════
// DIARY EXPORT — Markdown (for Obsidian / second brain) + PDF (print window)
// Pure module-level helpers, no external dependencies. PDF uses the same
// open-window-and-print pattern as VOProformaPDF.js.
// ═════════════════════════════════════════════════════════════════════════════

const DIARY_LABOUR = [
  { key: "foreman", label: "Foreman" },
  { key: "supervisor", label: "Supervisor" },
  { key: "driver", label: "Driver" },
  { key: "operator", label: "Operator" },
  { key: "artisan", label: "Artisan" },
  { key: "labourer", label: "Labourer" },
  { key: "trafficControl", label: "Traffic Control" },
];

const DIARY_PLANT = [
  { key: "tlb", label: "TLB" },
  { key: "excavator", label: "Excavator" },
  { key: "tipperTruck", label: "Tipper Truck" },
  { key: "waterCart", label: "Water Cart" },
  { key: "roller", label: "Roller" },
  { key: "generator", label: "Generator" },
];

// Resolve progress entries with the same legacy fallback the view modal uses.
function diaryProgressEntries(diary) {
  if (Array.isArray(diary.progressEntries) && diary.progressEntries.length > 0) return diary.progressEntries;
  if (diary.progressAssetId || diary.progressChainageStart || diary.progressChainageEnd) {
    return [{
      id: "legacy",
      progressAssetName: diary.progressAssetName,
      progressActivity: diary.progressActivity,
      progressStatus: diary.progressStatus,
      progressChainageStart: diary.progressChainageStart,
      progressChainageEnd: diary.progressChainageEnd,
      progressNotes: diary.progressNotes,
    }];
  }
  return [];
}

// Safe, vault-friendly filename stem (no slashes/quotes/whitespace clashes).
function diaryFileStem(diary) {
  const raw = String(diary.diaryNo || diary.id || "diary").trim();
  return raw.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, "_") || "diary";
}

function diaryHas(v) {
  return v !== null && v !== undefined && String(v).trim() !== "";
}

// ─── Markdown builder (Obsidian-ready with YAML frontmatter) ─────────────────
function buildDiaryMarkdown(diary, projectId) {
  const yamlStr = (v) => `"${String(v == null ? "" : v).replace(/"/g, '\\"')}"`;
  const entries = diaryProgressEntries(diary);
  const linked = Array.isArray(diary.linkedProgrammeActivities) ? diary.linkedProgrammeActivities : [];

  const lines = [];

  // YAML frontmatter — drives Dataview / properties in the vault.
  lines.push("---");
  lines.push("type: site-diary");
  lines.push(`diary_no: ${yamlStr(diary.diaryNo || diary.id || "")}`);
  lines.push(`date: ${yamlStr(diary.date || "")}`);
  lines.push(`project: ${yamlStr(projectId || diary.projectId || "")}`);
  if (diaryHas(diary.workSection)) lines.push(`work_section: ${yamlStr(diary.workSection)}`);
  if (diaryHas(diary.weather)) lines.push(`weather: ${yamlStr(diary.weather)}`);
  lines.push(`rainfall_mm: ${Number(diary.rainfallMm) || 0}`);
  lines.push(`contractor_signed: ${diary.contractorSigned === "Yes"}`);
  lines.push(`engineer_signed: ${diary.engineerSigned === "Yes"}`);
  lines.push(`locked: ${diary.isLocked === "Yes"}`);
  lines.push(`exported: ${yamlStr(new Date().toISOString())}`);
  lines.push("tags:");
  lines.push("  - site-diary");
  lines.push("  - daily-diary");
  lines.push("---");
  lines.push("");

  lines.push(`# Daily Site Diary — ${diary.diaryNo || diary.id || ""}`);
  lines.push("");

  // Header
  lines.push("## Header");
  lines.push(`- **Date:** ${diary.date || "—"}`);
  lines.push(`- **Work Section:** ${diaryHas(diary.workSection) ? diary.workSection : "—"}`);
  lines.push(`- **Weather:** ${diaryHas(diary.weather) ? diary.weather : "—"}`);
  lines.push(`- **Rainfall:** ${Number(diary.rainfallMm) || 0} mm`);
  lines.push("");

  // Programme activities
  if (linked.length > 0) {
    lines.push("## Programme Activities");
    linked.forEach((a) => {
      const id = a.activityId || a.id || "—";
      const status = a.activityStatus ? ` (${a.activityStatus})` : "";
      lines.push(`### ${id} — ${a.activityName || "Linked activity"}${status}`);
      if (diaryHas(a.diaryQuantity)) lines.push(`- **Quantity:** ${a.diaryQuantity} ${a.diaryQuantityUnit || ""}`.trim());
      if (diaryHas(a.linkedBoqItemCode)) lines.push(`- **BOQ:** ${a.linkedBoqItemCode}`);
      if (diaryHas(a.activityNote)) lines.push(`- **Note:** ${a.activityNote}`);
      lines.push("");
    });
  } else if (diaryHas(diary.linkedProgrammeActivityId)) {
    lines.push("## Programme Activities");
    lines.push(`### ${diary.linkedProgrammeActivityCode || diary.linkedProgrammeActivityId} — ${diary.linkedProgrammeActivityName || "Linked activity"}`);
    lines.push("");
  }

  // Progress entries
  if (entries.length > 0) {
    lines.push("## Progress Entries");
    entries.forEach((e, i) => {
      const start = Number(e.progressChainageStart);
      const end = Number(e.progressChainageEnd);
      const len = Number.isFinite(start) && Number.isFinite(end) ? Math.abs(end - start) : null;
      const status = diaryHas(e.progressStatus) ? ` (${e.progressStatus})` : "";
      lines.push(`### ${i + 1}. ${e.progressAssetName || "Unnamed asset"}${status}`);
      if (diaryHas(e.progressActivity)) lines.push(`- **Activity:** ${e.progressActivity}`);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        lines.push(`- **Chainage:** CH ${start} → CH ${end}${len != null ? ` (${len} m)` : ""}`);
      }
      if (diaryHas(e.progressNotes)) lines.push(`- **Notes:** ${e.progressNotes}`);
      lines.push("");
    });
  }

  // Site narrative
  if (diaryHas(diary.worksPerformed) || diaryHas(diary.generalComments)) {
    lines.push("## Site Narrative");
    if (diaryHas(diary.worksPerformed)) {
      lines.push("### Works Performed");
      lines.push(diary.worksPerformed);
      lines.push("");
    }
    if (diaryHas(diary.generalComments)) {
      lines.push("### General Comments");
      lines.push(diary.generalComments);
      lines.push("");
    }
  }

  // Labour
  const labourRows = DIARY_LABOUR.filter((l) => (Number(diary[l.key]) || 0) > 0);
  if (labourRows.length > 0) {
    lines.push("## Labour on Site");
    lines.push("| Role | Count |");
    lines.push("| --- | ---: |");
    labourRows.forEach((l) => lines.push(`| ${l.label} | ${Number(diary[l.key]) || 0} |`));
    lines.push("");
  }

  // Plant
  const plantRows = DIARY_PLANT.filter((p) => (Number(diary[p.key]) || 0) > 0);
  if (plantRows.length > 0) {
    lines.push("## Plant on Site");
    lines.push("| Plant | Count |");
    lines.push("| --- | ---: |");
    plantRows.forEach((p) => lines.push(`| ${p.label} | ${Number(diary[p.key]) || 0} |`));
    lines.push("");
  }

  // Signatures
  lines.push("## Signatures");
  lines.push(`- **Contractor:** ${diaryHas(diary.contractorSignName) ? diary.contractorSignName : "—"} · ${diary.contractorSigned === "Yes" ? "Signed" : "Not signed"}`);
  lines.push(`- **Engineer:** ${diaryHas(diary.engineerSignName) ? diary.engineerSignName : "—"} · ${diary.engineerSigned === "Yes" ? "Signed" : "Not signed"}`);
  lines.push("");

  lines.push("---");
  lines.push(`*Exported from Celestia · ${diary.id || ""}*`);
  lines.push("");

  return lines.join("\n");
}

// ─── Trigger a client-side file download ─────────────────────────────────────
function downloadTextFile(filename, text, mime = "text/plain") {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function exportDiaryMarkdown(diary, projectId) {
  const md = buildDiaryMarkdown(diary, projectId);
  downloadTextFile(`${diaryFileStem(diary)}.md`, md, "text/markdown");
}

// ─── PDF builder (styled print window → Save as PDF) ─────────────────────────
function exportDiaryPdf(diary, projectId) {
  const esc = (v) =>
    String(v == null ? "" : v)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#039;");

  const entries = diaryProgressEntries(diary);
  const linked = Array.isArray(diary.linkedProgrammeActivities) ? diary.linkedProgrammeActivities : [];
  const labourRows = DIARY_LABOUR.filter((l) => (Number(diary[l.key]) || 0) > 0);
  const plantRows = DIARY_PLANT.filter((p) => (Number(diary[p.key]) || 0) > 0);

  const sectionsHtml = [];

  sectionsHtml.push(`
    <table class="kv">
      <tr><th>Diary No.</th><td class="mono">${esc(diary.diaryNo || diary.id || "—")}</td>
          <th>Date</th><td class="mono">${esc(diary.date || "—")}</td></tr>
      <tr><th>Work Section</th><td>${esc(diary.workSection || "—")}</td>
          <th>Weather</th><td>${esc(diary.weather || "—")}</td></tr>
      <tr><th>Rainfall</th><td class="mono">${Number(diary.rainfallMm) || 0} mm</td>
          <th>Status</th><td>${diary.isLocked === "Yes" ? "Locked" : "Open"}</td></tr>
    </table>
  `);

  if (linked.length > 0) {
    const items = linked.map((a) => `
      <div class="entry">
        <div class="entry-head"><span class="mono accent">${esc(a.activityId || a.id || "—")}</span>
          ${a.activityStatus ? `<span class="pill">${esc(a.activityStatus)}</span>` : ""}</div>
        <div class="entry-title">${esc(a.activityName || "Linked activity")}</div>
        ${(a.diaryQuantity || a.linkedBoqItemCode) ? `<div class="entry-meta">${a.diaryQuantity ? `Qty: ${esc(a.diaryQuantity)} ${esc(a.diaryQuantityUnit || "")}` : ""}${a.diaryQuantity && a.linkedBoqItemCode ? " · " : ""}${a.linkedBoqItemCode ? `BOQ: ${esc(a.linkedBoqItemCode)}` : ""}</div>` : ""}
        ${a.activityNote ? `<div class="entry-note">${esc(a.activityNote)}</div>` : ""}
      </div>`).join("");
    sectionsHtml.push(`<h2>Programme Activities</h2>${items}`);
  }

  if (entries.length > 0) {
    const items = entries.map((e, i) => {
      const start = Number(e.progressChainageStart);
      const end = Number(e.progressChainageEnd);
      const len = Number.isFinite(start) && Number.isFinite(end) ? Math.abs(end - start) : null;
      const chain = Number.isFinite(start) && Number.isFinite(end)
        ? `CH ${start} → CH ${end}${len != null ? ` (${len} m)` : ""}` : "—";
      return `
        <div class="entry">
          <div class="entry-head"><span class="num">#${i + 1}</span>
            <span class="entry-title">${esc(e.progressAssetName || "Unnamed asset")}</span>
            ${e.progressStatus ? `<span class="pill">${esc(e.progressStatus)}</span>` : ""}</div>
          <div class="entry-meta">Activity: ${esc(e.progressActivity || "—")} · Chainage: <span class="mono">${esc(chain)}</span></div>
          ${e.progressNotes ? `<div class="entry-note">${esc(e.progressNotes)}</div>` : ""}
        </div>`;
    }).join("");
    sectionsHtml.push(`<h2>Progress Entries</h2>${items}`);
  }

  if (diary.worksPerformed || diary.generalComments) {
    sectionsHtml.push(`<h2>Site Narrative</h2>
      ${diary.worksPerformed ? `<div class="narr"><div class="narr-label">Works Performed</div><p>${esc(diary.worksPerformed).replace(/\n/g, "<br/>")}</p></div>` : ""}
      ${diary.generalComments ? `<div class="narr"><div class="narr-label">General Comments</div><p>${esc(diary.generalComments).replace(/\n/g, "<br/>")}</p></div>` : ""}`);
  }

  if (labourRows.length > 0) {
    const rows = labourRows.map((l) => `<tr><td>${esc(l.label)}</td><td class="mono right">${Number(diary[l.key]) || 0}</td></tr>`).join("");
    sectionsHtml.push(`<h2>Labour on Site</h2><table class="count"><tr><th>Role</th><th class="right">Count</th></tr>${rows}</table>`);
  }

  if (plantRows.length > 0) {
    const rows = plantRows.map((p) => `<tr><td>${esc(p.label)}</td><td class="mono right">${Number(diary[p.key]) || 0}</td></tr>`).join("");
    sectionsHtml.push(`<h2>Plant on Site</h2><table class="count"><tr><th>Plant</th><th class="right">Count</th></tr>${rows}</table>`);
  }

  sectionsHtml.push(`
    <h2>Signatures</h2>
    <table class="kv">
      <tr><th>Contractor</th><td>${esc(diary.contractorSignName || "—")} · ${diary.contractorSigned === "Yes" ? "Signed" : "Not signed"}</td></tr>
      <tr><th>Engineer</th><td>${esc(diary.engineerSignName || "—")} · ${diary.engineerSigned === "Yes" ? "Signed" : "Not signed"}</td></tr>
    </table>
  `);

  const html = `<!doctype html><html><head>
<meta charset="utf-8"/>
<title>Site Diary ${esc(diary.diaryNo || diary.id || "")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Inter, Arial, sans-serif; color: #1B3A57; margin: 0; padding: 0; background: #fff; }
  .sheet { max-width: 800px; margin: 0 auto; padding: 28px 32px 60px; }
  .topbar { display:flex; justify-content:space-between; align-items:flex-start; border-bottom: 3px solid #FF6B35; padding-bottom: 12px; margin-bottom: 18px; }
  .eyebrow { font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: #FF6B35; font-weight: 800; }
  h1 { font-size: 22px; margin: 4px 0 2px; letter-spacing: -0.02em; }
  .sub { font-size: 12px; color: #5b6b7a; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.6px; color: #1B3A57; border-bottom: 1px solid #e3e8ee; padding-bottom: 5px; margin: 22px 0 10px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
  table.kv th { text-align: left; width: 130px; font-size: 11px; color: #5b6b7a; font-weight: 700; padding: 5px 8px; vertical-align: top; }
  table.kv td { font-size: 12px; padding: 5px 8px; vertical-align: top; }
  table.count th, table.count td { font-size: 12px; padding: 5px 8px; border-bottom: 1px solid #eef1f4; text-align: left; }
  .right { text-align: right; }
  .mono { font-family: "JetBrains Mono", ui-monospace, Menlo, monospace; }
  .accent { color: #FF6B35; font-weight: 800; }
  .entry { border: 1px solid #e3e8ee; border-left: 3px solid #FF6B35; border-radius: 6px; padding: 8px 12px; margin-bottom: 8px; }
  .entry-head { display:flex; gap:8px; align-items:center; margin-bottom: 3px; }
  .entry-title { font-weight: 700; font-size: 13px; }
  .entry-meta { font-size: 11px; color: #5b6b7a; }
  .entry-note { font-size: 11px; color: #5b6b7a; font-style: italic; margin-top: 4px; }
  .num { font-family:"JetBrains Mono",monospace; font-size:10px; background:#f1f5f9; padding:2px 6px; border-radius:4px; }
  .pill { font-size:10px; font-weight:700; background:#eef2f6; color:#1B3A57; padding:2px 8px; border-radius:999px; text-transform:capitalize; }
  .narr { margin-bottom: 10px; }
  .narr-label { font-size: 11px; font-weight: 700; color: #5b6b7a; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 3px; }
  .narr p { margin: 0; font-size: 12px; line-height: 1.5; }
  .foot { margin-top: 30px; padding-top: 10px; border-top: 1px solid #e3e8ee; font-size: 10px; color: #93a1ad; }
  .btn-print { position: fixed; top: 16px; right: 16px; background: #FF6B35; color: #fff; border: none; padding: 10px 16px; border-radius: 8px; font-weight: 700; font-size: 13px; cursor: pointer; }
  @media print { .btn-print { display: none; } .sheet { padding-top: 10px; } }
</style>
</head>
<body>
  <button class="btn-print" onclick="window.print()">🖨 Print / Save as PDF</button>
  <div class="sheet">
    <div class="topbar">
      <div>
        <div class="eyebrow">Daily Site Diary</div>
        <h1>Diary #${esc(diary.diaryNo || diary.id || "—")}</h1>
        <div class="sub">${esc(diary.date || "")} · Project ${esc(projectId || diary.projectId || "—")}</div>
      </div>
      <div class="sub mono">${esc(diary.id || "")}</div>
    </div>
    ${sectionsHtml.join("\n")}
    <div class="foot">Exported from Celestia · ${new Date().toLocaleString("en-ZA")}</div>
  </div>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
</body></html>`;

  openHtmlWindow(html, { features: "width=900,height=1100" });
}

// ─── Cockpit checkbox tile — replaces the old plain checkbox ─────────────────
// Visual: outlined chip that turns accent-filled when checked. Big tap target.
const CheckChip = memo(function CheckChip({ label, checked, onChange, disabled }) {
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 12px",
      borderRadius: t.radius.sm,
      border: `1px solid ${checked ? t.color.accent : t.color.border}`,
      background: checked ? t.color.accentSoft : t.color.surface,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      transition: `all ${t.motion.fast}`,
      userSelect: "none",
    }}>
      {/* Visual checkbox — custom, replaces native */}
      <span style={{
        width: 16, height: 16, borderRadius: 3,
        border: `1.5px solid ${checked ? t.color.accent : t.color.borderStrong}`,
        background: checked ? t.color.accent : t.color.surface,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        transition: `all ${t.motion.fast}`,
      }}>
        {checked && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
            stroke={t.color.inkOnAccent} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )}
      </span>
      <input type="checkbox" checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }} />
      <span style={{
        fontSize: 13, fontWeight: checked ? 600 : 500,
        color: checked ? t.color.ink : t.color.inkMuted,
        transition: `color ${t.motion.fast}`,
      }}>{label}</span>
    </label>
  );
});

const CheckGrid = memo(function CheckGrid({ items, values, onToggle }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
      gap: 8,
    }}>
      {items.map((item) => (
        <CheckChip key={item.key} label={item.label}
          checked={!!values[item.key]}
          onChange={(value) => onToggle(item.key, value)} />
      ))}
    </div>
  );
});

// ─── Custom row block for "Other" placeholders (description + qty) ──────────
const OtherPlaceholderBlock = memo(function OtherPlaceholderBlock({ prefix, form, updateDiaryForm }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {[1, 2, 3, 4, 5].map((index) => {
        const labelKey = `${prefix}Other${index}Label`;
        const valueKey = `${prefix}Other${index}Value`;
        const hasContent = !!form[labelKey] || !!form[valueKey];
        return (
          <div key={`${prefix}-${index}`} style={{
            display: "grid", gridTemplateColumns: "1fr 140px",
            gap: 8, alignItems: "center",
          }}>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                fontFamily: t.font.mono, fontSize: 10, fontWeight: 700,
                color: hasContent ? t.color.accent : t.color.inkSubtle,
                letterSpacing: "0.02em",
                pointerEvents: "none",
              }}>{prefix.toUpperCase()}-{index}</span>
              <Input
                value={form[labelKey] || ""}
                onChange={(e) => updateDiaryForm(labelKey, e.target.value)}
                placeholder="Description"
                style={{ paddingLeft: 64 }} />
            </div>
            <Input
              value={form[valueKey] || ""}
              onChange={(e) => updateDiaryForm(valueKey, e.target.value)}
              placeholder="Qty / note" />
          </div>
        );
      })}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// DiaryViewModal — read-only popup for inspecting one diary in detail.
// Replaces the old generic handleViewItem flow. Modal renders into a portal,
// uses theme tokens, ESC to close, and groups fields into clear sections.
// ─────────────────────────────────────────────────────────────────────────────
const DiaryViewModal = memo(function DiaryViewModal({ diary, onClose, onEdit }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!diary) return null;

  const fmt = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);

  // Pull progress entries with legacy fallback so older records still display
  const progressEntries = (() => {
    if (Array.isArray(diary.progressEntries) && diary.progressEntries.length > 0) return diary.progressEntries;
    if (diary.progressAssetId || diary.progressChainageStart || diary.progressChainageEnd) {
      return [{
        id: "legacy",
        progressAssetName: diary.progressAssetName,
        progressActivity: diary.progressActivity,
        progressStatus: diary.progressStatus,
        progressChainageStart: diary.progressChainageStart,
        progressChainageEnd: diary.progressChainageEnd,
        progressNotes: diary.progressNotes,
      }];
    }
    return [];
  })();

  const labourItems = ["foreman", "supervisor", "driver", "operator", "artisan", "labourer", "trafficControl"];
  const labourCount = labourItems.reduce((sum, k) => sum + (Number(diary[k]) || 0), 0);

  const linkedActivities = Array.isArray(diary.linkedProgrammeActivities) ? diary.linkedProgrammeActivities : [];

  const content = (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(2, 6, 23, 0.78)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
        fontFamily: t.font.body,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 920, maxHeight: "94vh",
          background: t.color.surface,
          borderRadius: 18,
          border: `1px solid ${t.color.border}`,
          boxShadow: "0 60px 140px rgba(0,0,0,0.55)",
          display: "grid", gridTemplateRows: "auto auto 1fr auto",
          overflow: "hidden",
        }}
      >
        {/* Header bar */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 24px",
          background: `linear-gradient(135deg, ${t.color.ink} 0%, ${t.color.inkMuted || "#1e293b"} 100%)`,
          color: "#ffffff",
        }}>
          <div style={{ display: "grid", gap: 4 }}>
            <span style={{
              fontSize: 11, fontWeight: 800,
              color: "rgba(255,255,255,0.65)",
              textTransform: "uppercase", letterSpacing: 0.6,
            }}>
              Daily Site Diary
            </span>
            <h2 style={{
              margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em",
              color: "#ffffff", fontFamily: t.font.display,
            }}>
              Diary #{fmt(diary.diaryNo, diary.id)} · {fmt(diary.date)}
            </h2>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {diary.isLocked === "Yes" && (
              <span style={{
                fontSize: 11, fontWeight: 700,
                background: "rgba(245, 158, 11, 0.18)",
                color: "#fbbf24",
                border: "1px solid rgba(245, 158, 11, 0.45)",
                padding: "4px 10px", borderRadius: 999,
                textTransform: "uppercase", letterSpacing: 0.5,
              }}>🔒 Locked</span>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.12)",
                color: "#ffffff",
                width: 36, height: 36, borderRadius: 10,
                fontSize: 22, lineHeight: 1, cursor: "pointer",
                fontWeight: 700,
              }}
            >×</button>
          </div>
        </div>

        {/* Quick-stats ribbon */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          background: t.color.surfaceMuted || t.color.surface,
          borderBottom: `1px solid ${t.color.border}`,
        }}>
          <MetaTile label="Weather" value={fmt(diary.weather)} mono={false} />
          <MetaTile label="Rainfall" value={diary.rainfallMm ? `${diary.rainfallMm} mm` : "0 mm"} />
          <MetaTile label="Labour on site" value={labourCount.toString()} />
          <MetaTile
            label="Signatures"
            value={
              diary.contractorSigned === "Yes" && diary.engineerSigned === "Yes" ? "Both signed"
              : diary.contractorSigned === "Yes" ? "Contractor only"
              : diary.engineerSigned === "Yes" ? "Engineer only"
              : "Pending"
            }
            valueColour={
              diary.contractorSigned === "Yes" && diary.engineerSigned === "Yes" ? t.color.success || "#10b981"
              : t.color.warning || "#f59e0b"
            }
          />
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", padding: 24, display: "grid", gap: 18 }}>

          {/* Header section */}
          <ViewSection title="Header">
            <FieldGrid>
              <FieldRow label="Diary No." value={fmt(diary.diaryNo)} mono />
              <FieldRow label="Date" value={fmt(diary.date)} mono />
              <FieldRow label="Work Section" value={fmt(diary.workSection)} />
              <FieldRow label="Weather" value={fmt(diary.weather)} />
            </FieldGrid>
          </ViewSection>

          {/* Programme activities */}
          {(linkedActivities.length > 0 || diary.linkedProgrammeActivityId) && (
            <ViewSection title={`Programme Activities ${linkedActivities.length > 0 ? `(${linkedActivities.length})` : ""}`}>
              {linkedActivities.length > 0 ? (
                <div style={{ display: "grid", gap: 10 }}>
                  {linkedActivities.map((a) => (
                    <div
                      key={String(a.id || a.activityId || a.activityName)}
                      style={{
                        background: t.color.surface,
                        border: `1px solid ${t.color.border}`,
                        borderLeft: `3px solid ${t.color.accent}`,
                        borderRadius: 10,
                        padding: "10px 14px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontFamily: t.font.mono, fontSize: 12, fontWeight: 800, color: t.color.accent }}>
                          {a.activityId || a.id || "—"}
                        </span>
                        {a.activityStatus === "Started"  && <DiaryBadge variant="info"    size="sm">Started</DiaryBadge>}
                        {a.activityStatus === "Busy"     && <DiaryBadge variant="warning" size="sm">Busy</DiaryBadge>}
                        {a.activityStatus === "Finished" && <DiaryBadge variant="success" size="sm">Finished</DiaryBadge>}
                      </div>
                      <div style={{ fontSize: 13, color: t.color.ink, fontWeight: 600 }}>
                        {a.activityName || "Linked activity"}
                      </div>
                      {(a.diaryQuantity || a.linkedBoqItemCode) && (
                        <div style={{ fontSize: 11, color: t.color.inkMuted, marginTop: 4 }}>
                          {a.diaryQuantity ? `Quantity: ${a.diaryQuantity} ${a.diaryQuantityUnit || ""}` : ""}
                          {a.diaryQuantity && a.linkedBoqItemCode ? " · " : ""}
                          {a.linkedBoqItemCode ? `BOQ: ${a.linkedBoqItemCode}` : ""}
                        </div>
                      )}
                      {a.activityNote && (
                        <p style={{
                          margin: "6px 0 0", fontSize: 12, color: t.color.inkMuted,
                          fontStyle: "italic", lineHeight: 1.5,
                        }}>{a.activityNote}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <FieldGrid>
                  <FieldRow label="Activity ID" value={fmt(diary.linkedProgrammeActivityCode || diary.linkedProgrammeActivityId)} mono />
                  <FieldRow label="Activity Name" value={fmt(diary.linkedProgrammeActivityName)} />
                </FieldGrid>
              )}
            </ViewSection>
          )}

          {/* Progress entries */}
          {progressEntries.length > 0 && (
            <ViewSection title={`Progress Entries (${progressEntries.length})`}>
              <div style={{ display: "grid", gap: 8 }}>
                {progressEntries.map((entry, i) => {
                  const start = Number(entry.progressChainageStart);
                  const end = Number(entry.progressChainageEnd);
                  const len = Number.isFinite(start) && Number.isFinite(end) ? Math.abs(end - start) : null;
                  return (
                    <div
                      key={entry.id || i}
                      style={{
                        background: t.color.surface,
                        border: `1px solid ${t.color.border}`,
                        borderRadius: 10,
                        padding: "12px 14px",
                        display: "grid", gap: 8,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{
                          fontFamily: t.font.mono, fontSize: 11, fontWeight: 800,
                          background: t.color.surfaceMuted || "#f1f5f9",
                          color: t.color.ink,
                          padding: "3px 8px", borderRadius: 6,
                        }}>#{i + 1}</span>
                        <strong style={{ fontSize: 13, color: t.color.ink }}>
                          {fmt(entry.progressAssetName, "Unnamed asset")}
                        </strong>
                        {entry.progressStatus === "completed"  && <DiaryBadge variant="success" size="sm">Completed</DiaryBadge>}
                        {entry.progressStatus === "in_progress" && <DiaryBadge variant="info"    size="sm">In Progress</DiaryBadge>}
                        {entry.progressStatus === "delayed"     && <DiaryBadge variant="danger"  size="sm">Delayed</DiaryBadge>}
                        {entry.progressStatus === "rework"      && <DiaryBadge variant="warning" size="sm">Rework</DiaryBadge>}
                      </div>

                      <FieldGrid>
                        <FieldRow label="Activity" value={fmt(entry.progressActivity)} />
                        {isServiceActivity(entry.progressActivity) && (
                          <FieldRow
                            label="Service"
                            value={
                              `${serviceMeta(entry.progressServiceType || "").label}` +
                              (Number(entry.progressServiceDiameterMm) > 0 ? ` · Ø ${entry.progressServiceDiameterMm} mm` : "")
                            }
                          />
                        )}
                        {entry.progressActivity === "site_clearance" && (
                          <FieldRow
                            label="Sub-section"
                            value={(() => {
                              const meta = clearanceSubTypeMeta(entry.progressClearanceSubType || "");
                              return meta.key ? `${meta.prefix} ${meta.label}` : "—";
                            })()}
                          />
                        )}
                        {entry.progressActivity === "excavation" && (
                          <FieldRow
                            label="Material"
                            value={(() => {
                              const meta = excavationConditionMeta(entry.progressExcavationCondition || "");
                              return meta.key ? `${meta.icon} ${meta.label}` : "—";
                            })()}
                          />
                        )}
                        <FieldRow
                          label={entry.progressActivity === "services_crossing" ? "Stake value" : "Chainage"}
                          mono
                          value={
                            entry.progressActivity === "services_crossing"
                              ? (Number.isFinite(start) ? `CH ${start}` : "—")
                              : Number.isFinite(start) && Number.isFinite(end)
                                ? `CH ${start} → CH ${end}${len != null ? ` (${len} m)` : ""}`
                                : "—"
                          }
                        />
                      </FieldGrid>

                      {entry.progressNotes && (
                        <p style={{
                          margin: 0, fontSize: 12, color: t.color.inkMuted,
                          fontStyle: "italic", lineHeight: 1.5,
                          paddingTop: 6, borderTop: `1px dashed ${t.color.border}`,
                        }}>
                          {entry.progressNotes}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </ViewSection>
          )}

          {/* Narrative */}
          {(diary.worksPerformed || diary.generalComments) && (
            <ViewSection title="Site Narrative">
              {diary.worksPerformed && (
                <NarrativeBlock label="Works Performed" text={diary.worksPerformed} />
              )}
              {diary.generalComments && (
                <NarrativeBlock label="General Comments" text={diary.generalComments} />
              )}
            </ViewSection>
          )}

          {/* Labour breakdown */}
          {labourCount > 0 && (
            <ViewSection title="Labour on Site">
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 8,
              }}>
                {labourItems.map((k) => {
                  const n = Number(diary[k]) || 0;
                  if (n === 0) return null;
                  const labels = {
                    foreman: "Foreman", supervisor: "Supervisor", driver: "Driver",
                    operator: "Operator", artisan: "Artisan", labourer: "Labourer",
                    trafficControl: "Traffic Control",
                  };
                  return (
                    <div key={k} style={{
                      background: t.color.surface,
                      border: `1px solid ${t.color.border}`,
                      borderRadius: 8,
                      padding: "10px 12px",
                      display: "grid", gap: 2,
                    }}>
                      <span style={{ fontSize: 10, color: t.color.inkMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        {labels[k]}
                      </span>
                      <strong style={{ fontSize: 18, color: t.color.ink, fontFamily: t.font.mono }}>{n}</strong>
                    </div>
                  );
                })}
              </div>
            </ViewSection>
          )}

          {/* Signatures */}
          <ViewSection title="Signatures">
            <FieldGrid>
              <FieldRow
                label="Contractor"
                value={`${fmt(diary.contractorSignName)} ${diary.contractorSigned === "Yes" ? "· Signed" : "· Not signed"}`}
              />
              <FieldRow
                label="Engineer"
                value={`${fmt(diary.engineerSignName)} ${diary.engineerSigned === "Yes" ? "· Signed" : "· Not signed"}`}
              />
            </FieldGrid>
          </ViewSection>
        </div>

        {/* Footer actions */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
          padding: "14px 24px",
          borderTop: `1px solid ${t.color.border}`,
          background: t.color.surfaceMuted || t.color.surface,
        }}>
          <span style={{
            fontSize: 11, color: t.color.inkMuted,
            fontFamily: t.font.mono,
          }}>
            ID: {diary.id || "—"}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: `1px solid ${t.color.border}`,
                background: t.color.surface,
                color: t.color.ink,
                padding: "10px 18px",
                borderRadius: 10,
                fontSize: 13, fontWeight: 700,
                cursor: "pointer",
                fontFamily: t.font.body,
              }}
            >Close</button>
            <button
              type="button"
              onClick={() => onEdit?.(diary)}
              disabled={diary.isLocked === "Yes"}
              style={{
                border: "none",
                background: diary.isLocked === "Yes" ? t.color.border : t.color.ink,
                color: "#ffffff",
                padding: "10px 18px",
                borderRadius: 10,
                fontSize: 13, fontWeight: 800,
                cursor: diary.isLocked === "Yes" ? "not-allowed" : "pointer",
                fontFamily: t.font.body,
                opacity: diary.isLocked === "Yes" ? 0.5 : 1,
              }}
              title={diary.isLocked === "Yes" ? "Locked diaries can't be edited" : "Edit this diary"}
            >✎ Edit Diary</button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
});

// Sub-components for the modal — kept local because they're tightly themed.
function ViewSection({ title, children }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <h3 style={{
        margin: 0,
        fontSize: 12,
        fontWeight: 800,
        color: t.color.ink,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        paddingBottom: 6,
        borderBottom: `1px solid ${t.color.border}`,
        fontFamily: t.font.body,
      }}>{title}</h3>
      {children}
    </div>
  );
}

function FieldGrid({ children }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 10,
    }}>{children}</div>
  );
}

function FieldRow({ label, value, mono }) {
  return (
    <div style={{
      background: t.color.surface,
      border: `1px solid ${t.color.border}`,
      borderRadius: 8,
      padding: "8px 12px",
      display: "grid", gap: 2,
    }}>
      <span style={{
        fontSize: 10, color: t.color.inkMuted, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.4,
      }}>{label}</span>
      <span style={{
        fontSize: 13, color: t.color.ink, fontWeight: 600,
        fontFamily: mono ? t.font.mono : t.font.body,
        wordBreak: "break-word",
      }}>{value}</span>
    </div>
  );
}

function NarrativeBlock({ label, text }) {
  return (
    <div style={{
      background: t.color.surface,
      border: `1px solid ${t.color.border}`,
      borderRadius: 10,
      padding: "12px 14px",
    }}>
      <div style={{
        fontSize: 10, color: t.color.inkMuted, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6,
      }}>{label}</div>
      <p style={{
        margin: 0,
        whiteSpace: "pre-wrap",
        fontSize: 13, color: t.color.ink, lineHeight: 1.6,
      }}>{text}</p>
    </div>
  );
}

function MetaTile({ label, value, valueColour, mono = true }) {
  return (
    <div style={{
      padding: "14px 18px",
      borderRight: `1px solid ${t.color.border}`,
      display: "grid", gap: 4,
    }}>
      <span style={{
        fontSize: 10, color: t.color.inkMuted, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.5,
      }}>{label}</span>
      <strong style={{
        fontSize: 16, color: valueColour || t.color.ink, fontWeight: 800,
        fontFamily: mono ? t.font.mono : t.font.body,
      }}>{value}</strong>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AdvancedDiaryFilters — multi-asset selection + chainage-point search.
// A diary matches the chainage filter if any of its progressEntries (or its
// legacy single-entry fields) covers the requested chainage value.
// ─────────────────────────────────────────────────────────────────────────────
const AdvancedDiaryFilters = memo(function AdvancedDiaryFilters({
  assets,
  assetFilter,
  setAssetFilter,
  chainageQuery,
  setChainageQuery,
  open,
  setOpen,
  resultCount,
  totalCount,
}) {
  const hasAssetFilter = assetFilter.length > 0;
  const hasChainageFilter = chainageQuery !== "" && Number.isFinite(Number(chainageQuery));
  const anyActive = hasAssetFilter || hasChainageFilter;

  // Active-filter pill labels
  const activePills = [];
  if (hasAssetFilter) {
    const labels = assetFilter
      .map((id) => {
        const a = assets.find((x) => String(x.id) === String(id));
        return a?.assetName || a?.name || id;
      })
      .filter(Boolean);
    activePills.push({ key: "asset", label: `Asset: ${labels.join(", ")}`, clear: () => setAssetFilter([]) });
  }
  if (hasChainageFilter) {
    activePills.push({
      key: "chainage",
      label: `Chainage: CH ${Number(chainageQuery).toLocaleString("en-ZA")}`,
      clear: () => setChainageQuery(""),
    });
  }

  const toggleAsset = (id) => {
    const key = String(id);
    if (assetFilter.includes(key)) {
      setAssetFilter(assetFilter.filter((x) => x !== key));
    } else {
      setAssetFilter([...assetFilter, key]);
    }
  };

  const clearAll = () => {
    setAssetFilter([]);
    setChainageQuery("");
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {/* Header row — disclosure toggle + result count + active pills */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={{
            border: `1px solid ${t.color.border}`,
            background: anyActive ? t.color.accent : t.color.surface,
            color: anyActive ? "#ffffff" : t.color.ink,
            padding: "6px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: t.font.body,
          }}
        >
          {open ? "▾" : "▸"} Advanced Filters
          {anyActive && (
            <span style={{
              background: "rgba(255,255,255,0.22)",
              padding: "1px 7px",
              borderRadius: 999,
              fontSize: 11,
            }}>
              {activePills.length} active
            </span>
          )}
        </button>

        {/* Active filter pills */}
        {activePills.map((pill) => (
          <span
            key={pill.key}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: t.color.surface,
              border: `1px solid ${t.color.border}`,
              padding: "4px 6px 4px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: t.color.ink,
              fontFamily: t.font.body,
            }}
          >
            {pill.label}
            <button
              type="button"
              onClick={pill.clear}
              title="Remove this filter"
              style={{
                border: "none", background: t.color.border,
                color: t.color.ink, width: 18, height: 18,
                borderRadius: "50%", cursor: "pointer",
                fontSize: 11, lineHeight: 1, fontWeight: 800,
              }}
            >×</button>
          </span>
        ))}

        {anyActive && (
          <button
            type="button"
            onClick={clearAll}
            style={{
              border: `1px solid ${t.color.border}`,
              background: "transparent",
              color: t.color.inkMuted,
              padding: "4px 10px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: t.font.body,
            }}
          >
            Clear all
          </button>
        )}

        {/* Result count summary on the right */}
        <span style={{
          marginLeft: "auto",
          color: t.color.inkMuted,
          fontSize: 11,
          fontFamily: t.font.mono,
        }}>
          {resultCount} of {totalCount} diaries
        </span>
      </div>

      {/* Filter body — visible only when expanded */}
      {open && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          gap: 14,
          padding: 14,
          background: t.color.surfaceMuted || t.color.surface,
          border: `1px solid ${t.color.border}`,
          borderRadius: 12,
        }}>
          {/* Asset multi-select */}
          <div style={{ display: "grid", gap: 6 }}>
            <label style={{
              fontSize: 11, fontWeight: 700,
              color: t.color.ink, textTransform: "uppercase",
              letterSpacing: 0.4,
              fontFamily: t.font.body,
            }}>
              Asset / Pipeline
              <span style={{
                marginLeft: 6, color: t.color.inkMuted, fontWeight: 500,
                textTransform: "none", letterSpacing: 0,
              }}>(multi-select)</span>
            </label>

            {assets.length === 0 ? (
              <div style={{
                padding: 10, fontSize: 12, color: t.color.inkMuted,
                background: t.color.surface, border: `1px dashed ${t.color.border}`,
                borderRadius: 8,
                fontFamily: t.font.body,
              }}>
                No pipeline assets created yet. Create one in the Visual Progress Tracker.
              </div>
            ) : (
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 6,
                maxHeight: 140, overflowY: "auto",
                padding: 6,
                background: t.color.surface,
                border: `1px solid ${t.color.border}`,
                borderRadius: 8,
              }}>
                {assets.map((asset) => {
                  const id = String(asset.id);
                  const checked = assetFilter.includes(id);
                  const label = asset.assetName || asset.name || id;
                  const chRange = (asset.startChainage != null && asset.endChainage != null)
                    ? `CH ${asset.startChainage}–${asset.endChainage}`
                    : "";
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleAsset(id)}
                      style={{
                        border: `1px solid ${checked ? t.color.accent : t.color.border}`,
                        background: checked ? t.color.accent : t.color.surface,
                        color: checked ? "#ffffff" : t.color.ink,
                        padding: "5px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontFamily: t.font.body,
                      }}
                    >
                      <span style={{
                        width: 12, height: 12, borderRadius: 3,
                        border: `1.5px solid ${checked ? "#ffffff" : t.color.border}`,
                        background: checked ? "#ffffff" : "transparent",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        color: t.color.accent,
                        fontSize: 9, fontWeight: 900, lineHeight: 1,
                      }}>
                        {checked ? "✓" : ""}
                      </span>
                      {label}
                      {chRange && (
                        <span style={{
                          fontSize: 10, opacity: 0.8, fontWeight: 500,
                          fontFamily: t.font.mono,
                        }}>{chRange}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Chainage point search */}
          <div style={{ display: "grid", gap: 6 }}>
            <label style={{
              fontSize: 11, fontWeight: 700,
              color: t.color.ink, textTransform: "uppercase",
              letterSpacing: 0.4,
              fontFamily: t.font.body,
            }}>
              Chainage Point
              <span style={{
                marginLeft: 6, color: t.color.inkMuted, fontWeight: 500,
                textTransform: "none", letterSpacing: 0,
              }}>(finds diaries whose progress range covers this value)</span>
            </label>

            <Input
              type="number"
              value={chainageQuery}
              onChange={(e) => setChainageQuery(e.target.value)}
              placeholder="e.g. 1250"
            />

            <p style={{
              margin: 0, color: t.color.inkMuted,
              fontSize: 11, fontStyle: "italic",
              fontFamily: t.font.body,
            }}>
              Example: enter <span style={{ fontFamily: t.font.mono }}>1100</span> and the filter returns
              every diary that worked any range covering CH 1100 — e.g. a row recorded as
              CH 1000 → CH 1200.
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Overlap helpers — compare two chainage ranges and classify any collision.
// Adjacent endpoints (e.g. one ends where the next starts) are NOT overlaps —
// that's normal day-to-day progress.
// ─────────────────────────────────────────────────────────────────────────────
function computeOverlap(aLo, aHi, bLo, bHi) {
  const lo = Math.max(aLo, bLo);
  const hi = Math.min(aHi, bHi);
  // Strictly less than — touching endpoints don't count as overlap
  if (lo >= hi) return null;

  // Identical ranges (or one fully contained inside the other with shared endpoints)
  const identical = aLo === bLo && aHi === bHi;
  // Full containment (one range completely covers the other, even with different endpoints)
  const fullyContains = (aLo <= bLo && aHi >= bHi) || (bLo <= aLo && bHi >= aHi);

  if (identical || fullyContains) {
    return { severity: "duplicate", lo, hi };
  }
  return { severity: "overlap", lo, hi };
}

function addConflict(result, entryId, severity, conflictWith) {
  if (!result.byEntryId[entryId]) result.byEntryId[entryId] = [];
  result.byEntryId[entryId].push({ severity, conflictWith });
  result.hasAny = true;
  if (severity === "duplicate") result.hasDuplicate = true;
  if (severity === "overlap") result.hasOverlap = true;
}

// ═════════════════════════════════════════════════════════════════════════════
// DELETE CONFIRMATION MODAL
// Requires the user to type the word "delete" before the action is enabled.
// Diary deletion is irreversible — there is no soft-delete/recovery, so this
// guard exists to prevent accidental loss of a captured site record.
// ═════════════════════════════════════════════════════════════════════════════
const DeleteDiaryModal = memo(function DeleteDiaryModal({ diary, onCancel, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");
  const ready = confirmText.trim().toLowerCase() === "delete";

  if (!diary) return null;

  const submit = () => {
    if (ready) onConfirm();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15, 23, 32, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 440,
          background: t.color.surface,
          borderRadius: t.radius.md || t.radius.sm,
          border: `1px solid ${t.color.border}`,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          overflow: "hidden",
        }}
      >
        {/* Accent danger bar */}
        <div style={{ height: 3, background: t.color.danger || "#C0392B" }} />

        <div style={{ padding: "22px 24px", display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{
              fontFamily: t.font.display || t.font.body,
              fontSize: 17, fontWeight: 700, color: t.color.ink,
            }}>
              Delete diary entry?
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.55, color: t.color.inkMuted }}>
              You are about to permanently delete{" "}
              <strong style={{ color: t.color.ink, fontFamily: t.font.mono }}>
                {diary.diaryNo || "this diary"}
              </strong>
              {diary.date ? (
                <> dated <strong style={{ color: t.color.ink, fontFamily: t.font.mono }}>{diary.date}</strong></>
              ) : null}
              . This cannot be undone and the record cannot be recovered.
            </div>
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.color.inkMuted }}>
              Type <span style={{ fontFamily: t.font.mono, color: t.color.danger || "#C0392B", fontWeight: 700 }}>delete</span> to confirm
            </label>
            <Input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") onCancel();
              }}
              placeholder="delete"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
            <Button size="sm" variant="subtle" onClick={onCancel} icon={I.cancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={submit}
              disabled={!ready}
              icon={I.trash}
            >
              Delete entry
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function DiariesSection({
  diaryForm,
  setDiaryForm,
  handleAddDiary,
  diaries,
  programmeActivities = [],
  boqItems = [],
  progressAssets = [],
  currentProjectId,
  editingDiaryId,
  handleEditDiary,
  handleDeleteDiary,
  clearDiaryForm,
  handleCopyPreviousDiary,
  handleViewItem,
}) {
  const [diarySearchText, setDiarySearchText] = useState("");

  // Register sort — defaults to newest entries first.
  const [diarySortField, setDiarySortField] = useState("date");
  const [diarySortDirection, setDiarySortDirection] = useState("desc");

  // Captured Diaries register collapse state — defaults to open the first
  // time and persists per session.
  const [registerCollapsed, setRegisterCollapsed] = useState(false);

  // Modernised view modal — shows a polished read-only view of one diary.
  const [viewDiary, setViewDiary] = useState(null);

  // Delete confirmation — holds the diary row pending deletion. The user must
  // type "delete" in the modal before handleDeleteDiary is actually called.
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  // Form modal open/close state. Opens when the user clicks "New Diary" or
  // clicks Edit on a row. Closes on Save (via clearDiaryForm) or Cancel.
  const [formOpen, setFormOpen] = useState(false);

  // Wrap handleEditDiary so clicking Edit on a row also opens the modal.
  const openEditDiary = (diary) => {
    handleEditDiary(diary);
    setFormOpen(true);
  };

  // Wrap clearDiaryForm so Cancel/Save also closes the modal.
  const closeForm = () => {
    clearDiaryForm();
    setFormOpen(false);
  };

  // Wrap handleSaveDiaryAndProgress to close modal on success.
  const handleSaveAndClose = async () => {
    await handleSaveDiaryAndProgress();
    setFormOpen(false);
  };

  // Advanced diary filters: multi-asset + chainage point search.
  // Matches against each diary's progressEntries (the array under
  // "Progress Entries" on the diary form) as well as the legacy single-entry
  // fields, so existing records are still searchable.
  const [assetFilter, setAssetFilter] = useState([]);   // array of progressAsset ids
  const [chainageQuery, setChainageQuery] = useState(""); // single CH value (string for input)
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [programmeLinkPickerOpen, setProgrammeLinkPickerOpen] = useState(false);
  const [programmeLinkSearch, setProgrammeLinkSearch] = useState("");
  // Progress tracker — assets-worked-on flow. Picker popover + per-asset modal.
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [activeAssetModalId, setActiveAssetModalId] = useState(null);
  const rows = useMemo(
    () => diaries.filter((item) => item.projectId === currentProjectId),
    [diaries, currentProjectId]
  );

  const programmeRows = useMemo(
    () => programmeActivities.filter((item) => item.projectId === currentProjectId || !item.projectId),
    [programmeActivities, currentProjectId]
  );

  const selectedProgrammeActivities = useMemo(() => {
    const selectedIds = Array.isArray(diaryForm.linkedProgrammeActivities)
      ? diaryForm.linkedProgrammeActivities.map((item) => String(item.id || item.activityId || "").trim()).filter(Boolean)
      : String(diaryForm.linkedProgrammeActivityId || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

    if (!selectedIds.length) return [];

    return programmeRows
      .filter((item) => {
        const id = String(item.id || item.activityId || "").trim();
        const code = String(item.activityId || "").trim();
        return selectedIds.includes(id) || selectedIds.includes(code);
      })
      .map((activity) => {
        const key = String(activity.id || activity.activityId || "").trim();
        const stored = Array.isArray(diaryForm.linkedProgrammeActivities)
          ? diaryForm.linkedProgrammeActivities.find((item) => {
              const storedId = String(item.id || item.activityId || "").trim();
              const storedCode = String(item.activityId || "").trim();
              return storedId === key || storedCode === String(activity.activityId || "").trim();
            })
          : null;

        return {
          ...activity,
          diaryQuantity: stored?.diaryQuantity || "",
          diaryQuantityUnit: stored?.diaryQuantityUnit || "",
          linkedBoqItemId: stored?.linkedBoqItemId || "",
          linkedBoqItemCode: stored?.linkedBoqItemCode || "",
          linkedBoqItemDescription: stored?.linkedBoqItemDescription || "",
          activityStatus: stored?.activityStatus || "",
          activityNote: stored?.activityNote || "",
        };
      });
  }, [diaryForm.linkedProgrammeActivities, diaryForm.linkedProgrammeActivityId, programmeRows]);

  // Search-filtered programme rows for the link picker modal
  const filteredProgrammeRows = useMemo(() => {
    const q = programmeLinkSearch.trim().toLowerCase();
    if (!q) return programmeRows;
    return programmeRows.filter((activity) => {
      const haystack = [
        activity.activityId,
        activity.activityName,
        activity.id,
        activity.wbs,
        activity.wbsCode,
        activity.section,
        activity.phase,
        activity.location,
        activity.contractor,
        activity.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [programmeRows, programmeLinkSearch]);

  const filteredRows = useMemo(() => {
    const q = diarySearchText.trim().toLowerCase();
    const chQueryNum = chainageQuery !== "" ? Number(chainageQuery) : null;
    const chQueryActive = chQueryNum !== null && Number.isFinite(chQueryNum);
    const assetFilterActive = assetFilter.length > 0;

    // Build a list of every progress entry on a diary, including the legacy
    // single-entry fields, so we can run asset/chainage checks against them.
    const entriesFor = (row) => {
      const entries = Array.isArray(row.progressEntries) ? [...row.progressEntries] : [];
      // Add a synthesised legacy row if there's no entries array but there
      // are legacy single-entry fields populated.
      if (entries.length === 0 && (row.progressAssetId || row.progressChainageStart || row.progressChainageEnd)) {
        entries.push({
          progressAssetId: row.progressAssetId || "",
          progressAssetName: row.progressAssetName || "",
          progressActivity: row.progressActivity || "",
          progressStatus: row.progressStatus || "",
          progressChainageStart: row.progressChainageStart,
          progressChainageEnd: row.progressChainageEnd,
          progressNotes: row.progressNotes || "",
        });
      }
      return entries;
    };

    return rows.filter((row) => {
      // ----- Asset filter -----
      if (assetFilterActive) {
        const entries = entriesFor(row);
        const matchesAsset = entries.some((e) => assetFilter.includes(String(e.progressAssetId || "")));
        if (!matchesAsset) return false;
      }

      // ----- Chainage point filter -----
      // A diary matches if ANY of its progress entries covers the requested
      // chainage value. We accept inclusive bounds because clients usually
      // mean "is the entry working at this point", not strict open intervals.
      if (chQueryActive) {
        const entries = entriesFor(row);
        const matchesChainage = entries.some((e) => {
          const start = Number(e.progressChainageStart);
          const end = Number(e.progressChainageEnd);
          if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
          const lo = Math.min(start, end);
          const hi = Math.max(start, end);
          // If the user combined asset + chainage, only count entries whose
          // asset matches the asset filter (otherwise a tie-in on a different
          // asset at the same chainage could leak in).
          if (assetFilterActive && !assetFilter.includes(String(e.progressAssetId || ""))) return false;
          return chQueryNum >= lo && chQueryNum <= hi;
        });
        if (!matchesChainage) return false;
      }

      // ----- Text search -----
      if (!q) return true;

      const linkedActivitiesText = Array.isArray(row.linkedProgrammeActivities)
        ? row.linkedProgrammeActivities
            .map((item) => [item.id, item.activityId, item.activityName, item.activityStatus, item.activityNote].join(" "))
            .join(" ")
        : "";

      // Include the progress entries content in the text-search haystack too,
      // so typing an asset name or chainage in the text box still works.
      const progressText = entriesFor(row)
        .map((e) => [
          e.progressAssetName,
          e.progressActivity,
          e.progressStatus,
          e.progressChainageStart,
          e.progressChainageEnd,
          e.progressNotes,
        ].join(" "))
        .join(" ");

      const hay = [
        row.diaryNo,
        row.date,
        row.linkedProgrammeActivityId,
        row.linkedProgrammeActivityCode,
        row.linkedProgrammeActivityName,
        linkedActivitiesText,
        row.worksPerformed,
        row.generalComments,
        progressText,
      ].join(" ").toLowerCase();

      return hay.includes(q);
    });
  }, [rows, diarySearchText, assetFilter, chainageQuery]);

  const sortedRows = useMemo(() => {
    const dir = diarySortDirection === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      if (diarySortField === "rainfallMm") {
        return ((Number(a.rainfallMm) || 0) - (Number(b.rainfallMm) || 0)) * dir;
      }
      const av = String(a[diarySortField] || "");
      const bv = String(b[diarySortField] || "");
      return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [filteredRows, diarySortField, diarySortDirection]);

  const labourItems = [
    { key: "foreman",    label: "Foreman" },
    { key: "supervisor", label: "Supervisor" },
    { key: "driver",     label: "Driver" },
    { key: "operator",   label: "Operator" },
    { key: "artisan",    label: "Artisan" },
    { key: "labourer",   label: "Labourer" },
  ];

  const plantItems = [
    { key: "tlb",         label: "TLB" },
    { key: "excavator",   label: "Excavator" },
    { key: "tipperTruck", label: "Tipper Truck" },
    { key: "waterCart",   label: "Water Cart" },
    { key: "roller",      label: "Roller" },
    { key: "generator",   label: "Generator" },
  ];

  const updateDiaryForm = (field, value) => {
    // The user only selects the date — the diary number is always generated
    // from it as SD-YYYY-MM-DD (read-only in the form).
    if (field === "date") {
      setDiaryForm({
        ...diaryForm,
        date: value,
        diaryNo: value ? `SD-${value}` : "",
        projectId: currentProjectId,
      });
      return;
    }
    setDiaryForm({ ...diaryForm, [field]: value, projectId: currentProjectId });
  };

  const syncLinkedProgrammeActivities = (nextActivities) => {
    setDiaryForm({
      ...diaryForm,
      projectId: currentProjectId,
      linkedProgrammeActivities: nextActivities,
      linkedProgrammeActivityId: nextActivities.map((item) => item.id || item.activityId).filter(Boolean).join(", "),
      linkedProgrammeActivityCode: nextActivities.map((item) => item.activityId || item.id).filter(Boolean).join(", "),
      linkedProgrammeActivityName: nextActivities.map((item) => item.activityName).filter(Boolean).join(", "),
    });
  };

  const updateLinkedProgrammeActivity = (activityKey, checked) => {
    const selected = programmeRows.find((item) => {
      const id = String(item.id || item.activityId || "").trim();
      const code = String(item.activityId || "").trim();
      return id === String(activityKey || "").trim() || code === String(activityKey || "").trim();
    });

    const existing = Array.isArray(diaryForm.linkedProgrammeActivities)
      ? diaryForm.linkedProgrammeActivities
      : [];

    const nextActivities = checked && selected
      ? [
          ...existing.filter((item) => String(item.id || item.activityId || "").trim() !== String(selected.id || selected.activityId || "").trim()),
          {
            id: String(selected.id || selected.activityId || "").trim(),
            activityId: String(selected.activityId || "").trim(),
            activityName: String(selected.activityName || "").trim(),
            diaryQuantity: "",
            diaryQuantityUnit: "",
            linkedBoqItemId: "",
            linkedBoqItemCode: "",
            linkedBoqItemDescription: "",
            activityStatus: "",
            activityNote: "",
          },
        ]
      : existing.filter((item) => String(item.id || item.activityId || "").trim() !== String(activityKey || "").trim() && String(item.activityId || "").trim() !== String(activityKey || "").trim());

    syncLinkedProgrammeActivities(nextActivities);
  };

  const updateLinkedProgrammeActivityDetail = (activityKey, field, value) => {
    const existing = Array.isArray(diaryForm.linkedProgrammeActivities)
      ? diaryForm.linkedProgrammeActivities
      : [];

    const nextActivities = existing.map((item) => {
      const id = String(item.id || item.activityId || "").trim();
      const code = String(item.activityId || "").trim();
      if (id !== String(activityKey || "").trim() && code !== String(activityKey || "").trim()) return item;

      const nextItem = { ...item, [field]: value };

      if (field === "linkedBoqItemId") {
        const selectedBoq = boqItems.find((boq) => String(boq.id || boq.itemNo || boq.itemNumber || boq.code || "").trim() === String(value || "").trim());
        nextItem.linkedBoqItemId = value;
        nextItem.linkedBoqItemCode = selectedBoq ? String(selectedBoq.itemNo || selectedBoq.itemNumber || selectedBoq.code || selectedBoq.id || "").trim() : "";
        nextItem.linkedBoqItemDescription = selectedBoq ? String(selectedBoq.description || selectedBoq.itemDescription || selectedBoq.name || "").trim() : "";
      }

      return nextItem;
    });

    syncLinkedProgrammeActivities(nextActivities);
  };

  const updateNestedFlag = (group, key, value) =>
    setDiaryForm({
      ...diaryForm,
      projectId: currentProjectId,
      [group]: { ...(diaryForm[group] || {}), [key]: value },
    });

  const pipelineProgressAssets = useMemo(
    () => (progressAssets || []).filter((asset) => String(asset.assetType || "pipeline").toLowerCase() === "pipeline"),
    [progressAssets]
  );

  const progressActivityOptions = [
    { value: "tmp", label: "Traffic Management (TMP)" },
    { value: "site_clearance", label: "Site Clearance" },
    { value: "tar_cutting", label: "Tar Cutting" },
    { value: "tar_removing", label: "Tar Removing" },
    { value: "excavation", label: "Excavation" },
    { value: "dump_rock", label: "Dump Rock" },
    { value: "crusher", label: "Crusher" },
    { value: "bedding", label: "Bedding" },
    { value: "pipe_laying", label: "Pipe Laying" },
    { value: "welding", label: "Welding" },
    { value: "concrete_encasement", label: "Concrete Encasement" },
    { value: "culverts", label: "Culverts" },
    { value: "backfill", label: "Backfill" },
    { value: "testing", label: "Testing" },
    { value: "reinstatement", label: "Reinstatement" },
    { value: "services_adjoining", label: "Services (Adjoining)" },
    { value: "services_crossing", label: "Services (Crossing)" },
  ];

  // Existing-service classification offered when a services activity is picked.
  const serviceTypeOptions = SERVICE_TYPES.map((s) => ({ value: s.key, label: s.label }));

  const progressStatusOptions = [
    { value: "completed", label: "Completed" },
    { value: "in_progress", label: "In progress" },
    { value: "delayed", label: "Delayed" },
    { value: "rework", label: "Rework" },
  ];

  const isProgressTrackingEnabled = String(diaryForm.progressTrackingEnabled || "No") === "Yes";

  const progressEntries = useMemo(() => {
    if (Array.isArray(diaryForm.progressEntries) && diaryForm.progressEntries.length > 0) {
      return diaryForm.progressEntries;
    }

    const hasLegacyProgress =
      diaryForm.progressAssetId ||
      diaryForm.progressActivity ||
      diaryForm.progressStatus ||
      diaryForm.progressChainageStart ||
      diaryForm.progressChainageEnd ||
      diaryForm.progressNotes;

    if (!hasLegacyProgress) return [];

    return [{
      id: `legacy-${diaryForm.diaryNo || "progress"}`,
      progressAssetId: diaryForm.progressAssetId || "",
      progressAssetName: diaryForm.progressAssetName || "",
      progressAssetType: diaryForm.progressAssetType || "pipeline",
      progressActivity: diaryForm.progressActivity || "pipe_laying",
      progressStatus: diaryForm.progressStatus || "completed",
      progressChainageStart: diaryForm.progressChainageStart || "",
      progressChainageEnd: diaryForm.progressChainageEnd || "",
      progressNotes: diaryForm.progressNotes || "",
    }];
  }, [diaryForm]);

  // ──────────────────────────────────────────────────────────────────────────
  // Overlap detection — warn if any progress entry in the current diary
  // collides with an entry from a *different* diary on the same asset +
  // activity, or with another entry in this same form.
  //
  // Rules:
  //   - Severity "duplicate" — ranges are identical (or one strictly contains
  //                            the other and they share both endpoints).
  //                            Blocks save.
  //   - Severity "overlap"   — ranges strictly intersect (interior overlap).
  //                            Adjacent endpoints (e.g. ends at 10, next starts
  //                            at 10) do NOT count as overlap — that's normal
  //                            consecutive-day progress.
  //                            Warns but does not block.
  // ──────────────────────────────────────────────────────────────────────────
  const overlapReport = useMemo(() => {
    const result = {
      byEntryId: {},        // entryId -> [{ severity, conflictWith }, ...]
      hasAny: false,
      hasDuplicate: false,
      hasOverlap: false,
    };

    const currentDiaryId = diaryForm.id || "";
    // All other diaries' progress entries we need to check against.
    const otherEntries = [];
    rows.forEach((d) => {
      if (d.id && d.id === currentDiaryId) return; // skip own record when editing
      const entries = Array.isArray(d.progressEntries) ? d.progressEntries : [];
      // Legacy fallback
      const usable = entries.length > 0 ? entries : (
        d.progressAssetId || d.progressChainageStart || d.progressChainageEnd
          ? [{
              id: `legacy-${d.id}`,
              progressAssetId: d.progressAssetId,
              progressActivity: d.progressActivity,
              progressChainageStart: d.progressChainageStart,
              progressChainageEnd: d.progressChainageEnd,
            }]
          : []
      );
      usable.forEach((e) => {
        const s = Number(e.progressChainageStart);
        const en = Number(e.progressChainageEnd);
        if (!Number.isFinite(s) || !Number.isFinite(en)) return;
        otherEntries.push({
          diaryId: d.id,
          diaryNo: d.diaryNo || d.id,
          diaryDate: d.date || "",
          assetId: String(e.progressAssetId || ""),
          activity: String(e.progressActivity || ""),
          rangeLo: Math.min(s, en),
          rangeHi: Math.max(s, en),
        });
      });
    });

    // Entries within the current form we should self-check too
    const currentEntries = progressEntries
      .map((e) => {
        // Existing services are records of what is in the ground, not work
        // done — the same service can legitimately be logged on many diaries.
        if (isServiceActivity(String(e.progressActivity || ""))) return null;
        const s = Number(e.progressChainageStart);
        const en = Number(e.progressChainageEnd);
        if (!Number.isFinite(s) || !Number.isFinite(en) || en === s) return null;
        return {
          entryId: e.id,
          assetId: String(e.progressAssetId || ""),
          activity: String(e.progressActivity || ""),
          rangeLo: Math.min(s, en),
          rangeHi: Math.max(s, en),
        };
      })
      .filter(Boolean);

    // Check each current entry against everything else
    currentEntries.forEach((cur) => {
      if (!cur.assetId || !cur.activity) return; // can't check without asset+activity

      // Cross-diary checks
      otherEntries.forEach((other) => {
        if (other.assetId !== cur.assetId) return;
        if (other.activity !== cur.activity) return;
        const overlap = computeOverlap(cur.rangeLo, cur.rangeHi, other.rangeLo, other.rangeHi);
        if (!overlap) return;
        addConflict(result, cur.entryId, overlap.severity, {
          source: "other-diary",
          diaryId: other.diaryId,
          diaryNo: other.diaryNo,
          diaryDate: other.diaryDate,
          rangeLo: other.rangeLo,
          rangeHi: other.rangeHi,
          overlapLo: overlap.lo,
          overlapHi: overlap.hi,
        });
      });

      // Self-diary checks (don't compare an entry to itself)
      currentEntries.forEach((other) => {
        if (other.entryId === cur.entryId) return;
        if (other.assetId !== cur.assetId) return;
        if (other.activity !== cur.activity) return;
        const overlap = computeOverlap(cur.rangeLo, cur.rangeHi, other.rangeLo, other.rangeHi);
        if (!overlap) return;
        addConflict(result, cur.entryId, overlap.severity, {
          source: "same-diary",
          entryId: other.entryId,
          rangeLo: other.rangeLo,
          rangeHi: other.rangeHi,
          overlapLo: overlap.lo,
          overlapHi: overlap.hi,
        });
      });
    });

    return result;
  }, [rows, progressEntries, diaryForm.id]);

  const normaliseProgressEntries = (entries) => entries.map((entry, index) => {
    const asset = pipelineProgressAssets.find((item) => String(item.id) === String(entry.progressAssetId || ""));
    const activity = entry.progressActivity || "pipe_laying";
    const isCrossing = activity === "services_crossing";
    const start = Number(entry.progressChainageStart);
    const end = Number(entry.progressChainageEnd);
    // A crossing service sits at a single stake value — it has no length.
    const length = !isCrossing && Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0;

    return {
      id: entry.id || `progress-${Date.now()}-${index}`,
      progressAssetId: entry.progressAssetId || "",
      progressAssetName: asset?.assetName || asset?.name || entry.progressAssetName || "",
      progressAssetType: asset?.assetType || entry.progressAssetType || "pipeline",
      progressActivity: activity,
      progressStatus: entry.progressStatus || "completed",
      progressChainageStart: entry.progressChainageStart ?? "",
      progressChainageEnd: isCrossing ? "" : (entry.progressChainageEnd ?? ""),
      progressLength: length,
      // Existing services — type + diameter (only meaningful on service rows).
      progressServiceType: isServiceActivity(activity) ? (entry.progressServiceType || "") : "",
      progressServiceDiameterMm: isServiceActivity(activity) ? (entry.progressServiceDiameterMm ?? "") : "",
      // Site clearance is measured as two separate sub-sections.
      progressClearanceSubType:
        activity === "site_clearance" ? (entry.progressClearanceSubType || "") : "",
      // Wet or dry material changes both the rate and the items measured.
      progressExcavationCondition:
        activity === "excavation" ? (entry.progressExcavationCondition || "") : "",
      progressNotes: entry.progressNotes || "",
    };
  });

  const syncProgressEntries = (nextEntries) => {
    const cleaned = normaliseProgressEntries(nextEntries);
    const first = cleaned[0] || {};

    setDiaryForm({
      ...diaryForm,
      projectId: currentProjectId,
      progressEntries: cleaned,

      // Backwards-compatible fields retained so any existing save logic still sees the first row.
      progressAssetId: first.progressAssetId || "",
      progressAssetName: first.progressAssetName || "",
      progressAssetType: first.progressAssetType || "pipeline",
      progressActivity: first.progressActivity || "pipe_laying",
      progressStatus: first.progressStatus || "completed",
      progressChainageStart: first.progressChainageStart || "",
      progressChainageEnd: first.progressChainageEnd || "",
      progressLength: first.progressLength || 0,
      progressClearanceSubType: first.progressClearanceSubType || "",
      progressExcavationCondition: first.progressExcavationCondition || "",
      progressNotes: first.progressNotes || "",
    });
  };

  const duplicateProgressEntry = (entry) => {
    syncProgressEntries([
      ...progressEntries,
      { ...entry, id: `progress-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` },
    ]);
  };

  const removeProgressEntry = (entryId) => {
    syncProgressEntries(progressEntries.filter((entry) => entry.id !== entryId));
  };

  // ── Assets-worked-on flow ────────────────────────────────────────────────
  // Progress entries are grouped by the asset they belong to. "Add asset
  // worked on" registers an asset (with one blank entry) and opens its modal;
  // inside the modal the user adds/edits entries scoped to that asset alone.

  // Add a fresh blank entry bound to a specific asset.
  const addEntryForAsset = (assetId) => {
    const asset = pipelineProgressAssets.find((item) => String(item.id) === String(assetId));
    syncProgressEntries([
      ...progressEntries,
      {
        id: `progress-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        progressAssetId: asset?.id || assetId || "",
        progressAssetName: asset?.assetName || asset?.name || "",
        progressAssetType: asset?.assetType || "pipeline",
        progressActivity: "pipe_laying",
        progressStatus: "completed",
        progressChainageStart: "",
        progressChainageEnd: "",
        progressServiceType: "",
        progressServiceDiameterMm: "",
        progressClearanceSubType: "",
        progressExcavationCondition: "",
        progressNotes: "",
      },
    ]);
  };

  // Register an asset as worked on today, then open its entry modal.
  const addAssetWorkedOn = (assetId) => {
    addEntryForAsset(assetId);
    setAssetPickerOpen(false);
    setActiveAssetModalId(String(assetId));
  };

  // Remove an asset from today's list — drops every entry under it.
  const removeAssetWorkedOn = (assetId) => {
    syncProgressEntries(
      progressEntries.filter((entry) => String(entry.progressAssetId || "") !== String(assetId))
    );
    if (String(activeAssetModalId) === String(assetId)) setActiveAssetModalId(null);
  };

  // Group entries by asset id, preserving first-seen order.
  const assetGroups = useMemo(() => {
    const order = [];
    const map = new Map();
    progressEntries.forEach((entry) => {
      const id = String(entry.progressAssetId || "");
      if (!map.has(id)) {
        map.set(id, []);
        order.push(id);
      }
      map.get(id).push(entry);
    });
    return order.map((assetId) => {
      const entries = map.get(assetId);
      const asset = pipelineProgressAssets.find((item) => String(item.id) === String(assetId));
      const totalLength = entries.reduce((sum, entry) => {
        const start = Number(entry.progressChainageStart);
        const end = Number(entry.progressChainageEnd);
        return sum + (Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0);
      }, 0);
      const conflictCount = entries.reduce(
        (sum, entry) => sum + ((overlapReport.byEntryId[entry.id] || []).length > 0 ? 1 : 0),
        0
      );
      const hasDuplicate = entries.some((entry) =>
        (overlapReport.byEntryId[entry.id] || []).some((c) => c.severity === "duplicate")
      );
      return {
        assetId,
        assetName: asset?.assetName || asset?.name || "Unassigned pipeline",
        exists: !!asset,
        entries,
        totalLength,
        conflictCount,
        hasDuplicate,
      };
    });
  }, [progressEntries, pipelineProgressAssets, overlapReport]);

  // Assets not yet added to today's list — offered in the picker.
  const availableAssetsToAdd = useMemo(() => {
    const used = new Set(assetGroups.map((g) => String(g.assetId)));
    return pipelineProgressAssets.filter((asset) => !used.has(String(asset.id)));
  }, [pipelineProgressAssets, assetGroups]);

  const activeAssetGroup = useMemo(
    () => assetGroups.find((g) => String(g.assetId) === String(activeAssetModalId)) || null,
    [assetGroups, activeAssetModalId]
  );

  const updateProgressEntry = (entryId, field, value) => {
    const nextEntries = progressEntries.map((entry) => {
      if (entry.id !== entryId) return entry;

      const next = { ...entry, [field]: value };

      if (field === "progressAssetId") {
        const asset = pipelineProgressAssets.find((item) => String(item.id) === String(value || ""));
        next.progressAssetName = asset?.assetName || asset?.name || "";
        next.progressAssetType = asset?.assetType || "pipeline";
      }

      return next;
    });

    syncProgressEntries(nextEntries);
  };

  const progressTotalLength = useMemo(() => {
    return progressEntries.reduce((sum, entry) => {
      const start = Number(entry.progressChainageStart);
      const end = Number(entry.progressChainageEnd);
      return sum + (Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0);
    }, 0);
  }, [progressEntries]);

  const buildDiaryProgressKey = () => {
    const diaryNo = String(diaryForm.diaryNo || "").trim();
    const diaryDate = String(diaryForm.date || "").trim();
    return String(editingDiaryId || diaryForm.id || diaryNo || diaryDate || `unsaved-${Date.now()}`).trim();
  };

  const deleteExistingProgressSegmentsForDiary = async (diaryKey, diaryNo) => {
    if (!currentProjectId || (!diaryKey && !diaryNo && !editingDiaryId)) return;

    const progressRef = collection(db, "projects", currentProjectId, "progressSegments");
    const queries = [];

    if (diaryKey) queries.push(query(progressRef, where("diaryKey", "==", diaryKey)));
    if (editingDiaryId) queries.push(query(progressRef, where("diaryId", "==", editingDiaryId)));
    if (diaryNo) {
      queries.push(query(progressRef, where("diaryNo", "==", diaryNo)));
      queries.push(query(progressRef, where("sourceDiaryNo", "==", diaryNo)));
    }

    const seen = new Set();
    const batch = writeBatch(db);
    let deleteCount = 0;

    for (const q of queries) {
      const snap = await getDocs(q);
      snap.docs.forEach((item) => {
        if (seen.has(item.ref.path)) return;
        seen.add(item.ref.path);
        batch.delete(item.ref);
        deleteCount += 1;
      });
    }

    if (deleteCount > 0) await batch.commit();
  };

  const syncVisualProgressSegmentsToFirestore = async () => {
    if (!currentProjectId) return;

    const diaryNo = String(diaryForm.diaryNo || "").trim();
    const diaryDate = String(diaryForm.date || "").trim();
    const diaryKey = buildDiaryProgressKey();

    await deleteExistingProgressSegmentsForDiary(diaryKey, diaryNo);

    if (!isProgressTrackingEnabled) return;

    const validEntries = normaliseProgressEntries(progressEntries).filter((entry) => {
      const start = Number(entry.progressChainageStart);
      const end = Number(entry.progressChainageEnd);
      if (!entry.progressAssetId || !entry.progressActivity) return false;
      // A crossing service only needs its stake value.
      if (entry.progressActivity === "services_crossing") return Number.isFinite(start);
      return Number.isFinite(start) && Number.isFinite(end) && end > start;
    });

    if (!validEntries.length) return;

    const progressRef = collection(db, "projects", currentProjectId, "progressSegments");
    const batch = writeBatch(db);

    validEntries.forEach((entry, index) => {
      const start = Number(entry.progressChainageStart);
      const isCrossing = entry.progressActivity === "services_crossing";
      // Crossings are a point: end mirrors start so the segment stays drawable.
      const end = isCrossing ? start : Number(entry.progressChainageEnd);
      const diameter = Number(entry.progressServiceDiameterMm);
      const asset = pipelineProgressAssets.find((item) => String(item.id) === String(entry.progressAssetId));
      const segmentRef = doc(progressRef);

      batch.set(segmentRef, {
        projectId: currentProjectId,
        diaryKey,
        diaryId: editingDiaryId || diaryForm.id || diaryNo || diaryDate || "",
        diaryNo,
        sourceDiaryNo: diaryNo,
        diaryDate,
        entryId: entry.id || `progress-${index}`,
        assetId: entry.progressAssetId,
        assetName: asset?.assetName || asset?.name || entry.progressAssetName || "",
        assetType: asset?.assetType || entry.progressAssetType || "pipeline",
        activity: entry.progressActivity,
        status: entry.progressStatus || "completed",
        chainageStart: start,
        chainageEnd: end,
        length: end - start,
        serviceType: entry.progressServiceType || "",
        serviceDiameterMm: Number.isFinite(diameter) && diameter > 0 ? diameter : null,
        clearanceSubType: entry.progressClearanceSubType || "",
        excavationCondition: entry.progressExcavationCondition || "",
        notes: entry.progressNotes || diaryForm.worksPerformed || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await batch.commit();
  };

  const handleSaveDiaryAndProgress = async () => {
    try {
      // ─── Overlap guard ────────────────────────────────────────────────
      if (overlapReport.hasDuplicate) {
        alert(
          "Cannot save — one or more progress entries duplicate a chainage range " +
          "that has already been captured on another diary (same asset + activity).\n\n" +
          "Adjust the chainage range or remove the duplicate entries before saving."
        );
        return;
      }
      if (overlapReport.hasOverlap) {
        const proceed = window.confirm(
          "One or more progress entries partially overlap a range already captured.\n\n" +
          "This is sometimes legitimate (rework, remedial work, repairs) but is " +
          "unusual. Are you sure you want to save?"
        );
        if (!proceed) return;
      }

      if (typeof handleAddDiary === "function") {
        await handleAddDiary();
      }

      // The diary save stores the register entry. This second write creates the actual
      // visual tracker segment documents consumed by VisualProgressTracker.
      await syncVisualProgressSegmentsToFirestore();
    } catch (error) {
      console.error("Failed to save diary visual progress segments:", error);
      alert(error?.message || "Diary saved failed while syncing visual progress segments.");
    }
  };

  const isLocked = diaryForm.isLocked === "Yes";

  // ── KPI counts ────────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const total = rows.length;
    const signed = rows.filter((r) => r.contractorSigned === "Yes" && r.engineerSigned === "Yes").length;
    const pending = total - signed;
    const totalRain = rows.reduce((s, r) => s + (Number(r.rainfallMm) || 0), 0);
    return { total, signed, pending, totalRain };
  }, [rows]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <section style={{ position: "relative", display: "grid", gap: 18, fontFamily: t.font.body }}>
      <BlueprintGrid />

      <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 18 }}>

        {/* ───── PAGE HEADER ───── */}
        <Reveal delay={0}>
          <Card padded={false} style={{ borderRadius: 14, position: "relative" }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 40%, ${t.color.primary} 100%)`,
            }} />
            <div style={{ padding: "24px 26px", display: "flex",
              justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Eyebrow>Site Record</Eyebrow>
                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                  <span style={{
                    fontFamily: t.font.mono, fontSize: 11, fontWeight: 600,
                    color: t.color.inkMuted, letterSpacing: "0.02em",
                  }}>{currentProjectId || "—"}</span>
                </div>
                <h1 style={{
                  margin: 0, fontFamily: t.font.display,
                  fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1,
                  color: t.color.ink,
                }}>Daily Site Diary</h1>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5, maxWidth: 640 }}>
                  Record labour, plant, weather and works for each day on site, then
                  link the diary to programme activities for start, progress and finish tracking.
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="outline" onClick={() => { handleCopyPreviousDiary(); setFormOpen(true); }} icon={I.copy}>
                  Copy Previous Day
                </Button>
                <Button variant="accent" onClick={() => { clearDiaryForm(); setFormOpen(true); }} icon={I.add}>
                  New Diary
                </Button>
              </div>
            </div>
          </Card>
        </Reveal>

        {/* ───── KPI STRIP ───── */}
        <Reveal delay={120}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}>
            <Stat label="Diaries Logged" rawValue={counts.total}    format={(v) => Math.round(v)} value={counts.total}    accent={t.color.primary} delay={80}  helper="Total entries this project" />
            <Stat label="Fully Signed"   rawValue={counts.signed}   format={(v) => Math.round(v)} value={counts.signed}   accent={t.color.success} delay={140} helper="Contractor + engineer signed" />
            <Stat label="Awaiting Sign"  rawValue={counts.pending}  format={(v) => Math.round(v)} value={counts.pending}  accent={t.color.warning} delay={200} helper="Need one or both signatures" />
            <Stat label="Cumulative Rain" rawValue={counts.totalRain} format={(v) => `${v.toFixed(1)} mm`} value={`${counts.totalRain.toFixed(1)} mm`} accent={t.color.info} delay={260} helper="Total rainfall recorded" />
          </div>
        </Reveal>

        {/* ───── FORM MODAL (portal) ───── */}
        {formOpen && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            onClick={closeForm}
            style={{
              position: "fixed", inset: 0, zIndex: 9998,
              background: "rgba(15, 23, 32, 0.6)",
              display: "flex", alignItems: "flex-start", justifyContent: "center",
              padding: "40px 16px 40px",
              overflowY: "auto",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%", maxWidth: 860,
                background: t.color.surface,
                borderRadius: 14,
                border: `1px solid ${t.color.border}`,
                boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
                overflow: "hidden",
                marginBottom: 40,
              }}
            >
              {/* Accent bar */}
              <div style={{
                height: 3,
                background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 100%)`,
              }} />

              {/* Modal header */}
              <div style={{
                padding: "18px 22px 4px",
                display: "flex", alignItems: "flex-start",
                justifyContent: "space-between", gap: 12,
              }}>
                <div>
                  <Eyebrow>{editingDiaryId ? "Editing record" : "New record"}</Eyebrow>
                  <h3 style={{
                    margin: "6px 0 0", fontFamily: t.font.display,
                    fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
                  }}>Daily Diary Entry</h3>
                  {isLocked && (
                    <div style={{ marginTop: 8, display: "inline-flex" }}>
                      <DiaryBadge variant="warning">Diary Locked</DiaryBadge>
                    </div>
                  )}
                </div>
                <button
                  onClick={closeForm}
                  aria-label="Close"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: t.color.inkMuted, fontSize: 20, lineHeight: 1,
                    padding: "2px 6px", borderRadius: 6,
                    marginTop: 2,
                  }}
                >✕</button>
              </div>

            <div style={{ padding: "0 22px 22px" }}>
              <FormGroupDivider>Header</FormGroupDivider>

              <FormRow label="Date" helper="Select the date of site activities. The diary number is generated automatically.">
                <Input type="date" value={diaryForm.date || ""} onChange={(e) => updateDiaryForm("date", e.target.value)} />
              </FormRow>
              <FormRow label="Diary Number" helper="Auto-generated from the date as SD-YYYY-MM-DD.">
                <Input
                  mono
                  readOnly
                  value={diaryForm.diaryNo || ""}
                  placeholder="Select a date above"
                  style={{ background: t.color.surfaceSunken, cursor: "not-allowed", color: t.color.inkMuted }}
                />
              </FormRow>
              <FormRow label="Rainfall (mm)" helper="Rainfall recorded for the day">
                <Input type="number" value={diaryForm.rainfallMm || ""} onChange={(e) => updateDiaryForm("rainfallMm", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Programme Link</FormGroupDivider>

              <FormRow label="Programme Activity Measurement" helper="Open the table to select activities worked on, capture quantities and optionally link BOQ items">
                <div style={{ display: "grid", gap: 8 }}>
                  <Button variant="outline" onClick={() => setProgrammeLinkPickerOpen(true)}>
                    Open Programme Link Table ({selectedProgrammeActivities.length} selected)
                  </Button>
                  {selectedProgrammeActivities.length > 0 && (
                    <div style={{ display: "grid", gap: 6 }}>
                      {selectedProgrammeActivities.map((activity) => (
                        <div key={String(activity.id || activity.activityId || activity.activityName)} style={{
                          display: "grid",
                          gridTemplateColumns: "90px 1fr auto auto",
                          gap: 8,
                          alignItems: "center",
                          padding: "8px 10px",
                          borderRadius: t.radius.sm,
                          border: `1px solid ${t.color.border}`,
                          background: t.color.surface,
                        }}>
                          <span style={{ fontFamily: t.font.mono, fontSize: 11, fontWeight: 700, color: t.color.accent }}>
                            {activity.activityId || activity.id || "—"}
                          </span>
                          <div style={{ display: "grid", gap: 2, minWidth: 0 }}>
                            <span style={{ fontSize: 12, color: t.color.inkMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {activity.activityName || "Linked activity"}
                            </span>
                            {activity.activityNote && (
                              <span style={{ fontSize: 11, color: t.color.inkSubtle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {activity.activityNote}
                              </span>
                            )}
                          </div>
                          {activity.activityStatus && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
                              background:
                                activity.activityStatus === "Started"  ? t.color.infoSoft :
                                activity.activityStatus === "Busy"     ? t.color.warningSoft :
                                activity.activityStatus === "Finished" ? t.color.successSoft :
                                t.color.surfaceAlt,
                              color:
                                activity.activityStatus === "Started"  ? t.color.info :
                                activity.activityStatus === "Busy"     ? t.color.warning :
                                activity.activityStatus === "Finished" ? t.color.success :
                                t.color.inkMuted,
                              border: `1px solid ${
                                activity.activityStatus === "Started"  ? t.color.infoBorder :
                                activity.activityStatus === "Busy"     ? t.color.warningBorder :
                                activity.activityStatus === "Finished" ? t.color.successBorder :
                                t.color.border
                              }`,
                              whiteSpace: "nowrap",
                            }}>
                              {activity.activityStatus}
                            </span>
                          )}
                          <span style={{ fontFamily: t.font.mono, fontSize: 11, color: t.color.inkMuted }}>
                            {activity.diaryQuantity || "—"} {activity.diaryQuantityUnit || ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FormRow>

              {/* Removed old "Selected Activities" FormRow */}
        {programmeLinkPickerOpen && createPortal((
          <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "grid", placeItems: "center" }}>
            <div onClick={() => { setProgrammeLinkPickerOpen(false); setProgrammeLinkSearch(""); }} style={{ position: "absolute", inset: 0, background: "rgba(2, 6, 23, 0.72)", backdropFilter: "blur(6px)" }} />
            <div style={{
              position: "relative",
              width: "min(1300px, 96vw)",
              maxHeight: "86vh",
              overflow: "hidden",
              borderRadius: 16,
              border: `1px solid ${t.color.border}`,
              background: t.color.panel || t.color.surface,
              boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
              display: "grid",
              gridTemplateRows: "auto auto 1fr auto",
            }}>
              <div style={{ padding: "18px 20px", borderBottom: `1px solid ${t.color.border}`, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div>
                  <Eyebrow>Programme Link</Eyebrow>
                  <h3 style={{ margin: "6px 0 0", fontFamily: t.font.display, fontSize: 18, color: t.color.ink }}>
                    Activity Measurement Table
                  </h3>
                  <p style={{ margin: "6px 0 0", fontSize: 12, color: t.color.inkMuted }}>
                    Select programme activities worked on today, capture diary quantity, and optionally allocate a BOQ item for later measurement traceability.
                  </p>
                </div>
                <Button variant="subtle" onClick={() => { setProgrammeLinkPickerOpen(false); setProgrammeLinkSearch(""); }} icon={I.cancel}>Close</Button>
              </div>

              {/* Search bar */}
              <div style={{ padding: "12px 20px", borderBottom: `1px solid ${t.color.border}`, background: t.color.surfaceAlt, display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.color.inkMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", zIndex: 1 }}>
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <Input
                    value={programmeLinkSearch}
                    onChange={(e) => setProgrammeLinkSearch(e.target.value)}
                    placeholder="Search by activity ID, name, WBS, section, location…"
                    style={{ paddingLeft: 34 }}
                  />
                </div>
                {programmeLinkSearch && (
                  <Button variant="subtle" size="sm" onClick={() => setProgrammeLinkSearch("")}>Clear</Button>
                )}
                <span style={{ fontSize: 11, fontFamily: t.font.mono, color: t.color.inkMuted, whiteSpace: "nowrap" }}>
                  {filteredProgrammeRows.length} of {programmeRows.length}
                </span>
              </div>

              <div style={{ overflow: "auto", padding: 18 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 70 }}>Worked On</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted }}>Programme Item</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 140 }}>Status</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 160 }}>Quantity</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 240 }}>BOQ Allocation</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 240 }}>Activity Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProgrammeRows.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: "32px 10px", textAlign: "center", color: t.color.inkMuted, fontSize: 12 }}>
                          {programmeRows.length === 0
                            ? "No programme activities have been loaded for this project yet."
                            : `No activities match "${programmeLinkSearch}". Try a different search term or clear the search.`}
                        </td>
                      </tr>
                    )}
                    {filteredProgrammeRows.map((activity) => {
                      const key = String(activity.id || activity.activityId || "").trim();
                      const code = String(activity.activityId || "").trim();
                      const checked = selectedProgrammeActivities.some((item) => {
                        const selectedId = String(item.id || item.activityId || "").trim();
                        const selectedCode = String(item.activityId || "").trim();
                        return selectedId === key || selectedCode === code;
                      });
                      const selectedDetail = selectedProgrammeActivities.find((item) => {
                        const selectedId = String(item.id || item.activityId || "").trim();
                        const selectedCode = String(item.activityId || "").trim();
                        return selectedId === key || selectedCode === code;
                      });

                      return (
                        <tr key={key || code || activity.activityName} style={{ background: checked ? t.color.accentSoft : "transparent" }}>
                          <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => updateLinkedProgrammeActivity(key || code, e.target.checked)}
                              style={{ accentColor: t.color.accent }}
                            />
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                            <div style={{ display: "grid", gap: 2 }}>
                              <span style={{ fontFamily: t.font.mono, fontSize: 11, fontWeight: 700, color: checked ? t.color.accent : t.color.inkMuted }}>
                                {code || key || "—"}
                              </span>
                              <span style={{ color: checked ? t.color.ink : t.color.inkMuted }}>
                                {activity.activityName || "Unnamed activity"}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                            <Select
                              value={selectedDetail?.activityStatus || ""}
                              onChange={(e) => updateLinkedProgrammeActivityDetail(key || code, "activityStatus", e.target.value)}
                              disabled={!checked}
                            >
                              <option value="">—</option>
                              <option value="Started">Started today</option>
                              <option value="Busy">Busy / continued</option>
                              <option value="Finished">Finished today</option>
                            </Select>
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 60px", gap: 6 }}>
                              <Input
                                type="number"
                                value={selectedDetail?.diaryQuantity || ""}
                                onChange={(e) => updateLinkedProgrammeActivityDetail(key || code, "diaryQuantity", e.target.value)}
                                disabled={!checked}
                                placeholder="Qty"
                              />
                              <Input
                                value={selectedDetail?.diaryQuantityUnit || ""}
                                onChange={(e) => updateLinkedProgrammeActivityDetail(key || code, "diaryQuantityUnit", e.target.value)}
                                disabled={!checked}
                                placeholder="m³"
                              />
                            </div>
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                            <Select
                              value={selectedDetail?.linkedBoqItemId || ""}
                              onChange={(e) => updateLinkedProgrammeActivityDetail(key || code, "linkedBoqItemId", e.target.value)}
                              disabled={!checked || boqItems.length === 0}
                            >
                              <option value="">{boqItems.length ? "No BOQ item allocated" : "BOQ list not connected"}</option>
                              {boqItems.map((boq) => {
                                const boqKey = String(boq.id || boq.itemNo || boq.itemNumber || boq.code || "").trim();
                                const boqCode = String(boq.itemNo || boq.itemNumber || boq.code || boq.id || "").trim();
                                const boqDesc = String(boq.description || boq.itemDescription || boq.name || "").trim();
                                return <option key={boqKey || boqCode || boqDesc} value={boqKey}>{boqCode ? `${boqCode} — ${boqDesc}` : boqDesc}</option>;
                              })}
                            </Select>
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                            <Textarea
                              rows={2}
                              value={selectedDetail?.activityNote || ""}
                              onChange={(e) => updateLinkedProgrammeActivityDetail(key || code, "activityNote", e.target.value)}
                              disabled={!checked}
                              placeholder="e.g. Started trench excavation at CH 120; slower due to existing services."
                              style={{ minHeight: 42, fontSize: 12 }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ padding: "14px 18px", borderTop: `1px solid ${t.color.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 12, color: t.color.inkMuted }}>
                  {selectedProgrammeActivities.length} programme activit{selectedProgrammeActivities.length === 1 ? "y" : "ies"} linked to this diary.
                </span>
                <Button variant="accent" onClick={() => { setProgrammeLinkPickerOpen(false); setProgrammeLinkSearch(""); }}>Done</Button>
              </div>
            </div>
          </div>
        ), document.body)}

              <FormGroupDivider>Labour</FormGroupDivider>

              <FormRow label="Standard Labour Categories" helper="Tick labour categories present on site">
                <CheckGrid items={labourItems}
                  values={diaryForm.labourFlags || {}}
                  onToggle={(key, value) => updateNestedFlag("labourFlags", key, value)} />
              </FormRow>
              <FormRow label="Other Labour Placeholders" helper="Add up to 5 custom labour descriptions and notes or quantities">
                <OtherPlaceholderBlock prefix="labour" form={diaryForm} updateDiaryForm={updateDiaryForm} />
              </FormRow>
              <FormRow label="Staff Summary" helper="Summarise labour, trades and supervision numbers">
                <Textarea rows={3} value={diaryForm.staffSummary || ""} onChange={(e) => updateDiaryForm("staffSummary", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Plant and Equipment</FormGroupDivider>

              <FormRow label="Standard Plant Types" helper="Tick plant and equipment present on site">
                <CheckGrid items={plantItems}
                  values={diaryForm.plantFlags || {}}
                  onToggle={(key, value) => updateNestedFlag("plantFlags", key, value)} />
              </FormRow>
              <FormRow label="Other Plant Placeholders" helper="Add up to 5 custom plant descriptions and notes or quantities">
                <OtherPlaceholderBlock prefix="plant" form={diaryForm} updateDiaryForm={updateDiaryForm} />
              </FormRow>
              <FormRow label="Plant and Equipment Summary" helper="Summarise key plant and equipment on site">
                <Textarea rows={3} value={diaryForm.plantSummary || ""} onChange={(e) => updateDiaryForm("plantSummary", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Visual Progress Tracker</FormGroupDivider>

              <FormRow
                label="Send this diary to Visual Progress Tracker"
                helper="Use this for linear work such as pipeline progress by chainage. One diary can now contain multiple progress entries for different activity layers."
              >
                <Select
                  value={diaryForm.progressTrackingEnabled || "No"}
                  onChange={(e) => updateDiaryForm("progressTrackingEnabled", e.target.value)}
                  disabled={isLocked}
                >
                  <option value="No">No — normal diary only</option>
                  <option value="Yes">Yes — create/update progress segments</option>
                </Select>
              </FormRow>

              {isProgressTrackingEnabled && (
                <div style={{
                  display: "grid",
                  gap: 12,
                  padding: 14,
                  borderRadius: t.radius.md || t.radius.sm,
                  border: `1px solid ${t.color.border}`,
                  background: t.color.surface,
                  marginBottom: 16,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ display: "grid", gap: 3 }}>
                      <strong style={{ fontSize: 13, color: t.color.ink }}>Assets Worked On</strong>
                      <span style={{ fontSize: 12, color: t.color.inkMuted }}>
                        Add each asset (pipeline) worked on today, then open it to capture the chainage activities for that asset alone.
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", position: "relative" }}>
                      <DiaryBadge variant="info">{assetGroups.length} asset{assetGroups.length === 1 ? "" : "s"}</DiaryBadge>
                      <DiaryBadge variant="success">{progressTotalLength.toLocaleString("en-ZA")} m total</DiaryBadge>
                      <Button
                        variant="outline"
                        onClick={() => setAssetPickerOpen((open) => !open)}
                        disabled={isLocked || availableAssetsToAdd.length === 0}
                        icon={I.add}
                      >
                        Add Asset Worked On
                      </Button>

                      {/* Asset picker popover — lists assets not yet added today */}
                      {assetPickerOpen && availableAssetsToAdd.length > 0 && (
                        <>
                          <div
                            onClick={() => setAssetPickerOpen(false)}
                            style={{ position: "fixed", inset: 0, zIndex: 10 }}
                          />
                          <div style={{
                            position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 11,
                            minWidth: 240, maxHeight: 280, overflowY: "auto",
                            background: t.color.surface,
                            border: `1px solid ${t.color.border}`,
                            borderRadius: t.radius.sm,
                            boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
                            padding: 6,
                          }}>
                            <div style={{ padding: "4px 8px 6px", fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: t.color.inkMuted }}>
                              Select an asset
                            </div>
                            {availableAssetsToAdd.map((asset) => (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => addAssetWorkedOn(asset.id)}
                                style={{
                                  display: "block", width: "100%", textAlign: "left",
                                  padding: "8px 10px", border: "none", background: "transparent",
                                  borderRadius: t.radius.sm, cursor: "pointer",
                                  fontSize: 13, color: t.color.ink,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = t.color.surfaceMuted || "#f1f5f9"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                              >
                                {asset.assetName || asset.name || asset.id}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Overlap summary banner — appears when any entry has conflicts */}
                  {overlapReport.hasAny && (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 14px",
                      borderRadius: t.radius.sm,
                      background: overlapReport.hasDuplicate ? (t.color.dangerSoft || "#fef2f2") : (t.color.warningSoft || "#fef3c7"),
                      border: `1px solid ${overlapReport.hasDuplicate ? (t.color.danger || "#dc2626") : (t.color.warning || "#f59e0b")}`,
                      fontSize: 12,
                      color: overlapReport.hasDuplicate ? (t.color.danger || "#991b1b") : (t.color.warning || "#92400e"),
                    }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>
                        {overlapReport.hasDuplicate ? "🚫" : "⚠️"}
                      </span>
                      <div style={{ flex: 1, lineHeight: 1.5 }}>
                        <strong>
                          {overlapReport.hasDuplicate
                            ? "Duplicate progress detected"
                            : "Chainage overlap detected"}
                        </strong>
                        {" — "}
                        {overlapReport.hasDuplicate
                          ? "one or more entries below repeat a range that has already been captured. Save is blocked until this is resolved."
                          : "one or more entries below partially repeat a previously-captured range. You can still save (you'll be asked to confirm) if this is intentional rework."}
                      </div>
                    </div>
                  )}

                  {pipelineProgressAssets.length === 0 ? (
                    <div style={{
                      padding: "10px 12px",
                      borderRadius: t.radius.sm,
                      background: t.color.warningSoft || t.color.surface,
                      border: `1px solid ${t.color.warningDim || t.color.border}`,
                      color: t.color.warning || t.color.ink,
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}>
                      Create a pipeline asset in Visual Progress Tracker before saving diary progress to the schematic.
                    </div>
                  ) : assetGroups.length === 0 ? (
                    <DiaryEmpty
                      title="No assets added yet"
                      message="Click Add Asset Worked On to register a pipeline, then open it to capture today's chainage activities."
                    />
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {assetGroups.map((group) => {
                        const accent = group.hasDuplicate ? (t.color.danger || "#dc2626")
                                     : group.conflictCount > 0 ? (t.color.warning || "#f59e0b")
                                     : (t.color.accent || "#2563eb");
                        return (
                          <div key={group.assetId || "unassigned"} style={{
                              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                              padding: "12px 14px", borderRadius: t.radius.sm,
                              border: `1px solid ${t.color.border}`, borderLeft: `4px solid ${accent}`,
                              background: t.color.surface,
                            }}>
                            <button type="button" onClick={() => setActiveAssetModalId(String(group.assetId))}
                              style={{ flex: 1, minWidth: 200, textAlign: "left", border: "none",
                                background: "transparent", cursor: "pointer", display: "grid", gap: 3, padding: 0 }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: t.color.ink }}>{group.assetName}</span>
                              <span style={{ fontSize: 12, color: t.color.inkMuted }}>
                                {group.entries.length} entr{group.entries.length === 1 ? "y" : "ies"}{" · "}
                                {group.totalLength.toLocaleString("en-ZA")} m
                                {group.conflictCount > 0 && (
                                  <span style={{ color: accent, fontWeight: 600 }}>
                                    {" · "}{group.hasDuplicate ? "duplicate range" : "chainage overlap"}
                                  </span>
                                )}
                              </span>
                            </button>
                            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                              <Button variant="outline" size="sm" onClick={() => setActiveAssetModalId(String(group.assetId))} disabled={isLocked}>Open entries</Button>
                              <Button variant="subtle" size="sm" onClick={() => removeAssetWorkedOn(group.assetId)} disabled={isLocked}>Remove</Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Per-asset entry modal — progress rows scoped to a single asset */}
                  {activeAssetGroup && createPortal(
                    <div role="dialog" aria-modal="true" onClick={() => setActiveAssetModalId(null)}
                      style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(15,23,32,0.55)",
                        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 960,
                        maxHeight: "88vh", display: "flex", flexDirection: "column",
                        background: t.color.surface, borderRadius: t.radius.md || t.radius.sm,
                        border: `1px solid ${t.color.border}`, boxShadow: "0 24px 60px rgba(0,0,0,0.35)", overflow: "hidden" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                          padding: "16px 20px", borderBottom: `1px solid ${t.color.border}` }}>
                          <div style={{ display: "grid", gap: 2 }}>
                            <strong style={{ fontSize: 15, color: t.color.ink }}>{activeAssetGroup.assetName}</strong>
                            <span style={{ fontSize: 12, color: t.color.inkMuted }}>
                              Progress entries for this asset · {activeAssetGroup.entries.length} entr{activeAssetGroup.entries.length === 1 ? "y" : "ies"} · {activeAssetGroup.totalLength.toLocaleString("en-ZA")} m
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <Button variant="outline" size="sm" onClick={() => addEntryForAsset(activeAssetGroup.assetId)} disabled={isLocked} icon={I.add}>Add Progress Entry</Button>
                            <Button variant="accent" size="sm" onClick={() => setActiveAssetModalId(null)}>Done</Button>
                          </div>
                        </div>
                        <div style={{ overflow: "auto", padding: 16 }}>
                    <div style={{ overflowX: "auto", border: `1px solid ${t.color.border}`, borderRadius: t.radius.sm }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820, fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 160 }}>Activity Layer</th>
                            <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 130 }}>Status</th>
                            <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 110 }}>CH Start</th>
                            <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 110 }}>CH End</th>
                            <th style={{ textAlign: "right", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 90 }}>Length</th>
                            <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted }}>Notes</th>
                            <th style={{ textAlign: "right", padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, color: t.color.inkMuted, width: 150 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeAssetGroup.entries.map((entry) => {
                            const start = Number(entry.progressChainageStart);
                            const end = Number(entry.progressChainageEnd);
                            const isService = isServiceActivity(entry.progressActivity);
                            const isCrossing = entry.progressActivity === "services_crossing";
                            const isClearance = entry.progressActivity === "site_clearance";
                            const isExcavation = entry.progressActivity === "excavation";
                            const length = !isCrossing && Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0;
                            const hasInvalidRange = !isCrossing
                              && entry.progressChainageStart !== "" && entry.progressChainageEnd !== "" && length <= 0;
                            const svc = isService ? serviceMeta(entry.progressServiceType || "") : null;
                            const cellCaption = {
                              fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                              textTransform: "uppercase", color: t.color.inkMuted,
                            };

                            // Conflicts attached to this entry (from overlapReport memo)
                            const conflicts = overlapReport.byEntryId[entry.id] || [];
                            const hasDuplicate = conflicts.some((c) => c.severity === "duplicate");
                            const hasSoftOverlap = conflicts.some((c) => c.severity === "overlap");

                            // Row background — priority: invalid > duplicate > overlap
                            const rowBg = hasInvalidRange ? (t.color.dangerSoft || "#fef2f2")
                                        : hasDuplicate     ? (t.color.dangerSoft || "#fef2f2")
                                        : hasSoftOverlap   ? (t.color.warningSoft || "#fef3c7")
                                        : "transparent";

                            return (
                              <React.Fragment key={entry.id}>
                              <tr style={{ background: rowBg }}>
                                <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                                  <div style={{ display: "grid", gap: 6 }}>
                                    <Select
                                      value={entry.progressActivity || "pipe_laying"}
                                      onChange={(e) => updateProgressEntry(entry.id, "progressActivity", e.target.value)}
                                      disabled={isLocked}
                                    >
                                      {progressActivityOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                      ))}
                                    </Select>

                                    {/* Site clearance splits into its two measured sub-sections */}
                                    {isClearance && (
                                      <Select
                                        value={entry.progressClearanceSubType || ""}
                                        onChange={(e) => updateProgressEntry(entry.id, "progressClearanceSubType", e.target.value)}
                                        disabled={isLocked}
                                      >
                                        <option value="">— sub-section —</option>
                                        {CLEARANCE_SUBTYPES.map((sub) => (
                                          <option key={sub.key} value={sub.key}>
                                            {sub.prefix} {sub.label}
                                          </option>
                                        ))}
                                      </Select>
                                    )}

                                    {/* Wet or dry material — one click, because it
                                        gets asked on every excavation row. */}
                                    {isExcavation && (
                                      <div style={{ display: "flex", gap: 6 }}>
                                        {EXCAVATION_CONDITIONS.map((cond) => {
                                          const on = (entry.progressExcavationCondition || "") === cond.key;
                                          return (
                                            <button
                                              key={cond.key}
                                              type="button"
                                              disabled={isLocked}
                                              onClick={() =>
                                                updateProgressEntry(
                                                  entry.id,
                                                  "progressExcavationCondition",
                                                  on ? "" : cond.key
                                                )
                                              }
                                              title={`Mark this run as ${cond.label.toLowerCase()}`}
                                              style={{
                                                flex: 1,
                                                cursor: isLocked ? "not-allowed" : "pointer",
                                                border: `1px solid ${on ? cond.colour : t.color.border}`,
                                                background: on ? cond.soft : t.color.surface,
                                                color: on ? cond.colour : t.color.inkMuted,
                                                borderRadius: t.radius.sm,
                                                padding: "5px 8px",
                                                fontSize: 11,
                                                fontWeight: 800,
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {cond.icon} {cond.key === "wet" ? "Wet" : "Dry"}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Service classification — drives the colour in the tracker */}
                                    {isService && (
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{
                                          width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                                          background: svc.colour, border: "1px solid rgba(15,23,42,0.35)",
                                        }} />
                                        <Select
                                          value={entry.progressServiceType || ""}
                                          onChange={(e) => updateProgressEntry(entry.id, "progressServiceType", e.target.value)}
                                          disabled={isLocked}
                                        >
                                          <option value="">— service type —</option>
                                          {serviceTypeOptions.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                          ))}
                                        </Select>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                                  <Select
                                    value={entry.progressStatus || "completed"}
                                    onChange={(e) => updateProgressEntry(entry.id, "progressStatus", e.target.value)}
                                    disabled={isLocked}
                                  >
                                    {progressStatusOptions.map((option) => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </Select>
                                </td>
                                <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                                  <div style={{ display: "grid", gap: 4 }}>
                                    {isCrossing && <span style={cellCaption}>Stake value</span>}
                                    <Input
                                      type="number"
                                      value={entry.progressChainageStart || ""}
                                      onChange={(e) => updateProgressEntry(entry.id, "progressChainageStart", e.target.value)}
                                      disabled={isLocked}
                                      placeholder={isCrossing ? "Stake" : "CH start"}
                                    />
                                  </div>
                                </td>
                                <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                                  {/* A crossing has no end chainage — it takes a diameter instead. */}
                                  {isCrossing ? (
                                    <div style={{ display: "grid", gap: 4 }}>
                                      <span style={cellCaption}>Diameter (mm)</span>
                                      <Input
                                        type="number"
                                        value={entry.progressServiceDiameterMm || ""}
                                        onChange={(e) => updateProgressEntry(entry.id, "progressServiceDiameterMm", e.target.value)}
                                        disabled={isLocked}
                                        placeholder="e.g. 315"
                                      />
                                    </div>
                                  ) : (
                                    <div style={{ display: "grid", gap: 4 }}>
                                      <Input
                                        type="number"
                                        value={entry.progressChainageEnd || ""}
                                        onChange={(e) => updateProgressEntry(entry.id, "progressChainageEnd", e.target.value)}
                                        disabled={isLocked}
                                        placeholder="CH end"
                                      />
                                      {isService && (
                                        <Input
                                          type="number"
                                          value={entry.progressServiceDiameterMm || ""}
                                          onChange={(e) => updateProgressEntry(entry.id, "progressServiceDiameterMm", e.target.value)}
                                          disabled={isLocked}
                                          placeholder="Ø mm (optional)"
                                        />
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, textAlign: "right", fontFamily: t.font.mono, color: hasInvalidRange ? (t.color.danger || "#dc2626") : t.color.inkMuted }}>
                                  {isCrossing
                                    ? (Number(entry.progressServiceDiameterMm) > 0 ? `Ø ${entry.progressServiceDiameterMm} mm` : "point")
                                    : length > 0 ? `${length.toLocaleString("en-ZA")} m` : "0 m"}
                                </td>
                                <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}` }}>
                                  <Textarea
                                    rows={2}
                                    value={entry.progressNotes || ""}
                                    onChange={(e) => updateProgressEntry(entry.id, "progressNotes", e.target.value)}
                                    disabled={isLocked}
                                    placeholder="e.g. Excavation CH450 to CH469; services slowed progress near CH460."
                                    style={{ minHeight: 42, fontSize: 12 }}
                                  />
                                </td>
                                <td style={{ padding: "8px 10px", borderBottom: `1px solid ${t.color.border}`, textAlign: "right" }}>
                                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, flexWrap: "wrap" }}>
                                    <Button variant="subtle" size="sm" onClick={() => duplicateProgressEntry(entry)} disabled={isLocked}>Duplicate</Button>
                                    <Button variant="subtle" size="sm" onClick={() => removeProgressEntry(entry.id)} disabled={isLocked}>Remove</Button>
                                  </div>
                                </td>
                              </tr>

                              {/* Overlap warning row */}
                              {conflicts.length > 0 && (
                                <tr style={{ background: hasDuplicate ? (t.color.dangerSoft || "#fef2f2") : (t.color.warningSoft || "#fef3c7") }}>
                                  <td colSpan={7} style={{ padding: "8px 10px 10px 14px", borderBottom: `1px solid ${t.color.border}` }}>
                                    <div style={{ display: "grid", gap: 6 }}>
                                      <div style={{
                                        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                      }}>
                                        <span style={{
                                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                                          width: 20, height: 20, borderRadius: "50%",
                                          background: hasDuplicate ? (t.color.danger || "#dc2626") : (t.color.warning || "#f59e0b"),
                                          color: "#ffffff", fontWeight: 800, fontSize: 12, lineHeight: 1,
                                        }}>{hasDuplicate ? "✕" : "!"}</span>
                                        <strong style={{
                                          fontSize: 12,
                                          color: hasDuplicate ? (t.color.danger || "#991b1b") : (t.color.warning || "#92400e"),
                                          textTransform: "uppercase", letterSpacing: 0.3,
                                        }}>
                                          {hasDuplicate
                                            ? "Duplicate range — this work has already been captured"
                                            : "Chainage overlap — part of this range has already been captured"}
                                        </strong>
                                      </div>

                                      {conflicts.map((c, i) => {
                                        const w = c.conflictWith;
                                        return (
                                          <div key={i} style={{
                                            fontSize: 11,
                                            color: t.color.ink,
                                            paddingLeft: 28,
                                            fontFamily: t.font.body,
                                            lineHeight: 1.55,
                                          }}>
                                            <span style={{ fontFamily: t.font.mono, fontWeight: 700 }}>
                                              CH {w.overlapLo} → CH {w.overlapHi}
                                            </span>
                                            {" overlaps with "}
                                            {w.source === "other-diary" ? (
                                              <>
                                                <strong>Diary #{w.diaryNo}</strong>
                                                {w.diaryDate ? <> ({w.diaryDate})</> : null}
                                                {" — captured as CH "}
                                                <span style={{ fontFamily: t.font.mono }}>{w.rangeLo} → {w.rangeHi}</span>
                                              </>
                                            ) : (
                                              <>
                                                another entry in this same diary at <span style={{ fontFamily: t.font.mono }}>CH {w.rangeLo} → {w.rangeHi}</span>
                                              </>
                                            )}
                                          </div>
                                        );
                                      })}

                                      <div style={{ fontSize: 11, color: t.color.inkMuted, fontStyle: "italic", paddingLeft: 28 }}>
                                        {hasDuplicate
                                          ? "Adjust the chainage range or remove this entry before saving."
                                          : "If this is intentional rework or remedial work, you can continue and confirm at save."}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}

                  {progressEntries.length > 0 && (
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: t.color.inkMuted }}>
                      Each asset's rows are stored on the diary as <span style={{ fontFamily: t.font.mono }}>progressEntries</span>. The first row is also mirrored to the old single progress fields for backwards compatibility.
                    </div>
                  )}
                </div>
              )}

              <FormGroupDivider>Works and Comments</FormGroupDivider>

              <FormRow label="Brief Description of Works Performed" helper="Main activities completed on the day">
                <Textarea rows={3} value={diaryForm.worksPerformed || ""} onChange={(e) => updateDiaryForm("worksPerformed", e.target.value)} />
              </FormRow>
              <FormRow label="General Comments" helper="Additional comments, issues, delays or notable events">
                <Textarea rows={3} value={diaryForm.generalComments || ""} onChange={(e) => updateDiaryForm("generalComments", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Sign Off</FormGroupDivider>

              <FormRow label="Contractor Representative" helper="Name of the contractor representative signing the diary">
                <Input value={diaryForm.contractorSignName || ""}
                  onChange={(e) => updateDiaryForm("contractorSignName", e.target.value)}
                  disabled={isLocked} />
              </FormRow>
              <FormRow label="Contractor Signature Status" helper="Future PIN or sign-off status for contractor">
                <Select value={diaryForm.contractorSigned || "No"}
                  onChange={(e) => updateDiaryForm("contractorSigned", e.target.value)}
                  disabled={isLocked}>
                  <option value="No">Not signed</option>
                  <option value="Yes">Signed</option>
                </Select>
              </FormRow>
              <FormRow label="Engineer Representative" helper="Name of the engineer representative signing the diary">
                <Input value={diaryForm.engineerSignName || ""}
                  onChange={(e) => updateDiaryForm("engineerSignName", e.target.value)}
                  disabled={isLocked} />
              </FormRow>
              <FormRow label="Engineer Signature Status" helper="Future PIN or sign-off status for engineer">
                <Select value={diaryForm.engineerSigned || "No"}
                  onChange={(e) => updateDiaryForm("engineerSigned", e.target.value)}
                  disabled={isLocked}>
                  <option value="No">Not signed</option>
                  <option value="Yes">Signed</option>
                </Select>
              </FormRow>
              <FormRow label="Diary Locked" helper="Locks diary from editing once both signatures are received">
                <Input value={diaryForm.isLocked || "No"} readOnly />
              </FormRow>

              <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                <Button variant="accent" onClick={handleSaveAndClose} icon={editingDiaryId ? I.save : I.add}>
                  {editingDiaryId ? "Update Daily Site Diary" : "Save Daily Site Diary"}
                </Button>
                <Button variant="subtle" onClick={closeForm} icon={I.cancel}>
                  Cancel
                </Button>
              </div>
            </div>
            </div>
          </div>,
          document.body
        )}

        {/* ───── TABLE CARD ───── */}
        <Reveal delay={300}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            {/* Clickable rollup header — always visible, summarises live counts */}
            <button
              type="button"
              onClick={() => setRegisterCollapsed((v) => !v)}
              aria-expanded={!registerCollapsed}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "18px 22px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                textAlign: "left",
                borderBottom: registerCollapsed ? "none" : `1px solid ${t.color.border}`,
                fontFamily: t.font.body,
              }}
            >
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 30, height: 30, borderRadius: 8,
                background: t.color.surface,
                border: `1px solid ${t.color.border}`,
                color: t.color.ink,
                fontSize: 14, fontWeight: 800,
                transition: "transform 160ms ease",
                transform: registerCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
              }}>▾</span>

              <div style={{ display: "grid", gap: 2, flex: 1, minWidth: 0 }}>
                <Eyebrow>Register</Eyebrow>
                <h3 style={{
                  margin: 0, fontFamily: t.font.display,
                  fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
                }}>Captured Diaries</h3>
              </div>

              <div style={{
                display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              }}>
                <DiaryBadge variant="info">{rows.length} captured</DiaryBadge>
                {(() => {
                  const fullySigned = rows.filter((r) => r.contractorSigned === "Yes" && r.engineerSigned === "Yes").length;
                  return <DiaryBadge variant="success">{fullySigned} fully signed</DiaryBadge>;
                })()}
                {(() => {
                  const locked = rows.filter((r) => r.isLocked === "Yes").length;
                  return locked > 0 ? <DiaryBadge variant="warning">{locked} locked</DiaryBadge> : null;
                })()}
                {(assetFilter.length > 0 || (chainageQuery !== "" && Number.isFinite(Number(chainageQuery)))) && (
                  <DiaryBadge variant="info">Filter active</DiaryBadge>
                )}
              </div>

              <span style={{
                color: t.color.inkMuted,
                fontSize: 11,
                fontFamily: t.font.body,
                fontWeight: 600,
                marginLeft: 4,
              }}>
                {registerCollapsed ? "Show" : "Hide"}
              </span>
            </button>

            {/* Collapsible body */}
            {!registerCollapsed && (
              <>
            <div style={{ padding: "20px 22px 16px", display: "grid", gap: 12 }}>
              <Input
                value={diarySearchText}
                onChange={(e) => setDiarySearchText(e.target.value)}
                placeholder="Search diary no, date, activity ID, activity name, status, notes, works or comments…"
              />

              {/* ──── Sort controls ─── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                {[
                  { label: "Sort By", value: diarySortField, onChange: setDiarySortField, options: [
                    { v: "date", l: "Date" },
                    { v: "diaryNo", l: "Diary number" },
                    { v: "rainfallMm", l: "Rainfall" },
                  ] },
                  { label: "Direction", value: diarySortDirection, onChange: setDiarySortDirection, options: [
                    { v: "desc", l: "Descending (newest first)" },
                    { v: "asc", l: "Ascending (oldest first)" },
                  ] },
                ].map((f) => (
                  <div key={f.label} style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: t.color.inkSubtle }}>{f.label}</span>
                    <Select value={f.value} onChange={(e) => f.onChange(e.target.value)}>
                      {f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </Select>
                  </div>
                ))}
              </div>

              {/* ──── Advanced filters: asset + chainage point search ─── */}
              <AdvancedDiaryFilters
                assets={pipelineProgressAssets}
                assetFilter={assetFilter}
                setAssetFilter={setAssetFilter}
                chainageQuery={chainageQuery}
                setChainageQuery={setChainageQuery}
                open={advancedFiltersOpen}
                setOpen={setAdvancedFiltersOpen}
                resultCount={filteredRows.length}
                totalCount={rows.length}
              />
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              {rows.length === 0 ? (
                <DiaryEmpty
                  title="No diary entries yet"
                  message="Capture the first daily site diary using the form above. Use 'Copy Previous Day' once you have a baseline entry."
                />
              ) : (
                <>
                <Table>
                  <thead>
                    <tr>
                      <Th>Diary No</Th>
                      <Th>Date</Th>
                      <Th>Programme Activity</Th>
                      <Th align="right">Rainfall</Th>
                      <Th align="center">Contractor</Th>
                      <Th align="center">Engineer</Th>
                      <Th align="center">Locked</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => (
                      <tr key={row.id}>
                        <Td mono weight={700}>{row.diaryNo || "—"}</Td>
                        <Td mono>{row.date || "—"}</Td>
                        <Td>
                          {Array.isArray(row.linkedProgrammeActivities) && row.linkedProgrammeActivities.length > 0 ? (
                            <div style={{ display: "grid", gap: 6 }}>
                              {row.linkedProgrammeActivities.map((activity) => (
                                <div key={String(activity.id || activity.activityId || activity.activityName)} style={{ display: "grid", gap: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                    <span style={{ fontFamily: t.font.mono, fontSize: 11, fontWeight: 700, color: t.color.accent }}>
                                      {activity.activityId || activity.id || "—"}
                                    </span>
                                    {activity.activityStatus === "Started"  && <DiaryBadge variant="info"    size="sm">Started</DiaryBadge>}
                                    {activity.activityStatus === "Busy"     && <DiaryBadge variant="warning" size="sm">Busy</DiaryBadge>}
                                    {activity.activityStatus === "Finished" && <DiaryBadge variant="success" size="sm">Finished</DiaryBadge>}
                                  </div>
                                  <span style={{ fontSize: 12, color: t.color.inkMuted }}>
                                    {activity.activityName || "Linked activity"}
                                  </span>
                                  {(activity.diaryQuantity || activity.linkedBoqItemCode) && (
                                    <span style={{ fontSize: 11, color: t.color.inkSubtle }}>
                                      {activity.diaryQuantity ? `Qty: ${activity.diaryQuantity} ${activity.diaryQuantityUnit || ""}` : ""}
                                      {activity.diaryQuantity && activity.linkedBoqItemCode ? " · " : ""}
                                      {activity.linkedBoqItemCode ? `BOQ: ${activity.linkedBoqItemCode}` : ""}
                                    </span>
                                  )}
                                  {activity.activityNote && (
                                    <span style={{ fontSize: 11, color: t.color.inkSubtle, fontStyle: "italic" }}>
                                      {activity.activityNote}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : row.linkedProgrammeActivityId ? (
                            <div style={{ display: "grid", gap: 2 }}>
                              <span style={{ fontFamily: t.font.mono, fontSize: 11, fontWeight: 700, color: t.color.accent }}>
                                {row.linkedProgrammeActivityCode || row.linkedProgrammeActivityId}
                              </span>
                              <span style={{ fontSize: 12, color: t.color.inkMuted }}>
                                {row.linkedProgrammeActivityName || "Linked activity"}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: t.color.inkSubtle }}>—</span>
                          )}
                        </Td>
                        <Td align="right" mono>{row.rainfallMm ? `${row.rainfallMm} mm` : "0 mm"}</Td>
                        <Td align="center">
                          {row.contractorSigned === "Yes"
                            ? <DiaryBadge variant="success" size="sm">Signed</DiaryBadge>
                            : <DiaryBadge variant="default" size="sm">Pending</DiaryBadge>}
                        </Td>
                        <Td align="center">
                          {row.engineerSigned === "Yes"
                            ? <DiaryBadge variant="success" size="sm">Signed</DiaryBadge>
                            : <DiaryBadge variant="default" size="sm">Pending</DiaryBadge>}
                        </Td>
                        <Td align="center">
                          {row.isLocked === "Yes"
                            ? <DiaryBadge variant="warning" size="sm">Locked</DiaryBadge>
                            : <span style={{ color: t.color.inkSubtle }}>—</span>}
                        </Td>
                        <Td align="right">
                          <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <Button size="sm" variant="success" onClick={() => setViewDiary(row)} icon={I.view}>
                              View
                            </Button>
                            <Button size="sm" variant="subtle" onClick={() => openEditDiary(row)} icon={I.edit}>
                              Edit
                            </Button>
                            <Button size="sm" variant="subtle" onClick={() => exportDiaryMarkdown(row, currentProjectId)} icon={I.md}>
                              MD
                            </Button>
                            <Button size="sm" variant="subtle" onClick={() => exportDiaryPdf(row, currentProjectId)} icon={I.pdf}>
                              PDF
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => setDeleteCandidate(row)} icon={I.trash}>
                              Delete
                            </Button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {rows.length > 0 && filteredRows.length === 0 && (
                  <div style={{ marginTop: 12 }}>
                    <DiaryEmpty
                      title="No linked diaries found"
                      message="No diary entries match your search. Try searching by activity ID, activity name, diary number, status, works performed or programme note."
                    />
                  </div>
                )}
                </>
              )}
            </div>
              </>
            )}
          </Card>
        </Reveal>

        {/* Modernised view modal */}
        {viewDiary && (
          <DiaryViewModal
            diary={viewDiary}
            onClose={() => setViewDiary(null)}
            onEdit={(d) => { setViewDiary(null); openEditDiary(d); }}
          />
        )}

        {/* Type-to-confirm delete modal — guards against accidental record loss */}
        {deleteCandidate && (
          <DeleteDiaryModal
            diary={deleteCandidate}
            onCancel={() => setDeleteCandidate(null)}
            onConfirm={() => {
              const id = deleteCandidate.id;
              setDeleteCandidate(null);
              handleDeleteDiary(id);
            }}
          />
        )}

      </div>
    </section>
  );
}

export default memo(DiariesSection);